import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

const NOTE_COLORS = [
  { id:'blue',  bg:'#E0F5FC', accent:'#AFE1F6', text:'#0f2a38' },
  { id:'sage',  bg:'#dceadd', accent:'#819D84', text:'#1a3020' },
  { id:'tan',   bg:'#f2e8d5', accent:'#B99C6D', text:'#3a2a10' },
  { id:'teal',  bg:'#d4e8f0', accent:'#6F8FA7', text:'#0f2030' },
  { id:'white', bg:'#ffffff', accent:'#d4dfe4', text:'#0f1e28' },
];
const DEFAULT_COLOR = NOTE_COLORS[0];
const BOARD_COLORS = ['#314F5C','#819D84','#B99C6D','#6F8FA7','#5856d6','#e60023','#229ED9'];
const SNAP_GRID = 28;

const TEMPLATES = [
  { id:'campaign', name:'Campaign Plan', emoji:'📣', desc:'Audience, copy, channels & timeline', color:'#9eb8c8',
    nodes:[
      { node_type:'heading', type:'heading', x:60, y:40, w:320, h:110, content:'Campaign Name', subtitle:'Edit this heading', color:'#d4e8f0', z_index:1 },
      { node_type:'section', type:'section', x:40, y:180, w:200, h:180, content:'Audience', color:'#E0F5FC', z_index:2 },
      { node_type:'section', type:'section', x:260, y:180, w:200, h:180, content:'Copy & Messaging', color:'#E0F5FC', z_index:3 },
      { node_type:'section', type:'section', x:480, y:180, w:200, h:180, content:'Channels', color:'#E0F5FC', z_index:4 },
      { node_type:'checklist', type:'checklist', x:40, y:390, w:220, h:200, content:'', color:'#dceadd', z_index:5, checked_items:[{text:'Set go-live date',done:false},{text:'Brief design',done:false},{text:'Schedule sends',done:false},{text:'Set up tracking',done:false}] },
      { node_type:'note', type:'note', x:280, y:390, w:200, h:120, content:'Success metrics here', color:'#f2e8d5', z_index:6 },
    ]},
  { id:'location', name:'Location Audit', emoji:'🗺️', desc:'Data quality review for a region', color:'#819D84',
    nodes:[
      { node_type:'heading', type:'heading', x:60, y:40, w:300, h:110, content:'Location Audit', subtitle:'Region / Country', color:'#dceadd', z_index:1 },
      { node_type:'checklist', type:'checklist', x:40, y:180, w:230, h:240, content:'', color:'#dceadd', z_index:2, checked_items:[{text:'GPS coordinates present',done:false},{text:'Phone numbers formatted',done:false},{text:'Types tagged correctly',done:false},{text:'No duplicates',done:false},{text:'Website verified',done:false}] },
      { node_type:'note', type:'note', x:290, y:180, w:200, h:150, content:'Flagged issues go here', color:'#f2e8d5', z_index:3 },
      { node_type:'note', type:'note', x:290, y:350, w:200, h:120, content:'Next steps', color:'#E0F5FC', z_index:4 },
    ]},
  { id:'retro', name:'Sprint Retro', emoji:'🔄', desc:'What went well, what didn’t, actions', color:'#B99C6D',
    nodes:[
      { node_type:'heading', type:'heading', x:60, y:40, w:300, h:110, content:'Sprint Retro', subtitle:'Add sprint name & date', color:'#f2e8d5', z_index:1 },
      { node_type:'section', type:'section', x:40, y:180, w:190, h:240, content:'✅ Went Well', color:'#dceadd', z_index:2 },
      { node_type:'section', type:'section', x:250, y:180, w:190, h:240, content:'⚠️ Improve', color:'#f2e8d5', z_index:3 },
      { node_type:'checklist', type:'checklist', x:460, y:180, w:200, h:240, content:'', color:'#E0F5FC', z_index:4, checked_items:[{text:'Action item 1',done:false},{text:'Action item 2',done:false},{text:'Action item 3',done:false}] },
    ]},
];

const SETUP_SQL = `-- Run this in Supabase SQL Editor:

CREATE TABLE IF NOT EXISTS public.studio_boards (
  id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
  name TEXT NOT NULL DEFAULT 'Untitled board',
  color TEXT DEFAULT '#314F5C',
  created_by TEXT DEFAULT '',
  created_at TIMESTAMPTZ DEFAULT NOW()
);
ALTER TABLE public.studio_boards ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Studio boards all" ON public.studio_boards FOR ALL USING (true) WITH CHECK (true);

CREATE TABLE IF NOT EXISTS public.studio_nodes (
  id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
  board_id UUID REFERENCES public.studio_boards(id) ON DELETE CASCADE,
  node_type TEXT DEFAULT 'note',
  type TEXT DEFAULT 'note',
  x FLOAT DEFAULT 100, y FLOAT DEFAULT 100,
  w FLOAT DEFAULT 200, h FLOAT DEFAULT 160,
  content TEXT DEFAULT '',
  subtitle TEXT DEFAULT '',
  color TEXT DEFAULT '#E0F5FC',
  checked_items JSONB DEFAULT '[]',
  z_index INTEGER DEFAULT 1,
  image_url TEXT,
  created_by TEXT DEFAULT '',
  updated_at TIMESTAMPTZ DEFAULT NOW()
);
ALTER TABLE public.studio_nodes ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Studio nodes all" ON public.studio_nodes FOR ALL USING (true) WITH CHECK (true);
ALTER PUBLICATION supabase_realtime ADD TABLE public.studio_nodes;

CREATE TABLE IF NOT EXISTS public.studio_edges (
  id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
  board_id UUID REFERENCES public.studio_boards(id) ON DELETE CASCADE,
  from_id UUID NOT NULL, to_id UUID NOT NULL,
  color TEXT DEFAULT '#314F5C',
  created_at TIMESTAMPTZ DEFAULT NOW()
);
ALTER TABLE public.studio_edges ENABLE ROW LEVEL SECURITY;
CREATE POLICY "Studio edges all" ON public.studio_edges FOR ALL USING (true) WITH CHECK (true);

INSERT INTO public.studio_boards (id, name) VALUES ('00000000-0000-0000-0000-000000000001', 'Main board') ON CONFLICT DO NOTHING;

-- Planner integration: link tasks to Studio boards
ALTER TABLE public.admin_tasks ADD COLUMN IF NOT EXISTS studio_board_id UUID REFERENCES public.studio_boards(id) ON DELETE SET NULL;

ALTER TABLE public.studio_nodes ADD COLUMN IF NOT EXISTS locked BOOLEAN DEFAULT false;`;

// ── Normalise mouse/touch coords ─────────────────────────────────────────────
function getEventPos(e) {
  if (e.touches && e.touches.length > 0) return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
  if (e.changedTouches && e.changedTouches.length > 0) return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
  return { clientX: e.clientX, clientY: e.clientY };
}

// ── Live cursor ───────────────────────────────────────────────────────────────
function LiveCursor({ c }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  return (
    <div style={{ position:'absolute', left:c.x, top:c.y, pointerEvents:'none', zIndex:9999, transform:'translate(4px,4px)' }}>
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l10 4-4.5 1.5L5 11 1 1z" fill={c.color||'#314F5C'} stroke="#fff" strokeWidth="1"/></svg>
      <div style={{ marginTop:2, padding:'2px 7px', background:c.color||'#314F5C', color:'#fff', borderRadius:99, fontSize:10, fontWeight:700, fontFamily:F, whiteSpace:'nowrap', boxShadow:'0 1px 4px rgba(0,0,0,0.18)' }}>{c.name}</div>
    </div>
  );
}

// ── Connector layer ───────────────────────────────────────────────────────────
function ConnectorLayer({ edges, nodes, connecting, mousePos }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });
  function mid(node) { return { x: node.x + node.w/2, y: node.y + node.h/2 }; }
  return (
    <svg style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', pointerEvents:'none', overflow:'visible' }}>
      <defs>
        <marker id="arr" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill="#314F5C" opacity="0.55"/>
        </marker>
        <marker id="arr-preview" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
          <path d="M0,0 L0,6 L8,3 z" fill="#314F5C" opacity="0.3"/>
        </marker>
      </defs>
      {edges.map(edge => {
        const from = nodeMap[edge.from_id], to = nodeMap[edge.to_id];
        if (!from || !to) return null;
        const f = mid(from), t = mid(to), mx = (f.x+t.x)/2;
        return <path key={edge.id} d={`M ${f.x} ${f.y} C ${mx} ${f.y}, ${mx} ${t.y}, ${t.x} ${t.y}`}
          stroke={edge.color||'#314F5C'} strokeWidth="1.8" strokeOpacity="0.5" fill="none"
          strokeDasharray="5 3" markerEnd="url(#arr)"/>;
      })}
      {connecting?.fromId && mousePos && (() => {
        const from = nodeMap[connecting.fromId];
        if (!from) return null;
        const f = mid(from), mx = (f.x+mousePos.x)/2;
        return <path d={`M ${f.x} ${f.y} C ${mx} ${f.y}, ${mx} ${mousePos.y}, ${mousePos.x} ${mousePos.y}`}
          stroke="#314F5C" strokeWidth="1.5" strokeOpacity="0.3" fill="none" strokeDasharray="4 3"
          markerEnd="url(#arr-preview)"/>;
      })()}
    </svg>
  );
}

// ── Delete button (shared) ────────────────────────────────────────────────────
function DelBtn({ onDelete }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  return (
    <button onMouseDown={e => { e.stopPropagation(); onDelete(); }}
      style={{ width:16, height:16, borderRadius:5, border:'none', background:'rgba(198,40,40,0.12)', color:'#c62828', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, flexShrink:0 }}>
      <svg width="7" height="7" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/></svg>
    </button>
  );
}

// ── Resize handle (shared) ────────────────────────────────────────────────────
function ResizeHandle({ visible, onMouseDown }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  return (
    <div onMouseDown={onMouseDown}
      style={{ position:'absolute', bottom:3, right:3, width:14, height:14, cursor:'nwse-resize', opacity: visible ? 0.55 : 0.12, transition:'opacity .12s' }}>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M11 5V11H5" stroke="#314F5C" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M11 8V11H8" stroke="#314F5C" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    </div>
  );
}

// ── Studio node ───────────────────────────────────────────────────────────────
function StudioNode({ node, selected, editing, editingCheckIdx, connectMode,
  onDragStart, onSelect, onEdit, onEditCheckIdx, onBlur, onContentChange,
  onSubtitleChange, onColorChange, onDelete, onResizeStart, onConnectClick,
  onCheckToggle, onAddCheckItem, onContextMenu, onPushToPlanner, onToggleLock }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars

  const [hovered, setHovered] = useState(false);
  const colorObj = NOTE_COLORS.find(c => c.bg === node.color) || DEFAULT_COLOR;
  const nt = node.node_type || node.type || 'note';
  const isImage = nt === 'image';
  const isSection = nt === 'section';
  const isDrawing = nt === 'drawing';
  const isHeading = nt === 'heading';
  const isChecklist = nt === 'checklist';
  const isLink = nt === 'link';
  const isLocked = !!node.locked;
  const showControls = (hovered || selected) && !connectMode;

  // ── Section ─────────────────────────────────────────────────────────────────
  if (isSection) {
    return (
      <div
        style={{ position:'absolute', left:node.x, top:node.y, width:node.w, height:node.h,
          borderRadius:12, border:`2px dashed ${selected ? '#314F5C' : 'rgba(49,79,92,0.2)'}`,
          background:'rgba(49,79,92,0.03)', cursor:'default', userSelect:'none' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onPointerDown={e => { if(e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId); onDragStart(e); }}
        onClick={onSelect}
        onContextMenu={e => { e.preventDefault(); onContextMenu && onContextMenu(e); }}>
        {editing ? (
          <input autoFocus value={node.content}
            onChange={e => onContentChange(e.target.value)}
            onBlur={onBlur}
            style={{ position:'absolute', top:8, left:12, width:'calc(100% - 56px)', border:'none', outline:'none', background:'transparent', fontFamily:F, fontSize:11, fontWeight:700, color:'rgba(49,79,92,0.5)', letterSpacing:'.04em', textTransform:'uppercase' }}/>
        ) : (
          <div onDoubleClick={e => { e.stopPropagation(); onSelect(); onEdit(); }}
            style={{ position:'absolute', top:8, left:12, right:30, fontFamily:F, fontSize:11, fontWeight:700, color:'rgba(49,79,92,0.4)', letterSpacing:'.04em', textTransform:'uppercase', cursor:'default' }}>
            {node.content || 'Section'}
          </div>
        )}
        {showControls && (
          <div style={{ position:'absolute', top:6, right:6 }}>
            <DelBtn onDelete={onDelete}/>
          </div>
        )}
        <ResizeHandle visible={showControls} onMouseDown={e => { e.stopPropagation(); onResizeStart(e, node); }}/>
      </div>
    );
  }

  // ── Drawing ──────────────────────────────────────────────────────────────────
  if (isDrawing) {
    return (
      <div style={{ position:'absolute', left:node.x, top:node.y, width:Math.max(node.w,10), height:Math.max(node.h,10),
        cursor:'default', userSelect:'none', pointerEvents:'all' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onPointerDown={e => { if(e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId); onDragStart(e); }}
        onClick={onSelect}
        onContextMenu={e => { e.preventDefault(); onContextMenu && onContextMenu(e); }}>
        <svg width={Math.max(node.w,10)} height={Math.max(node.h,10)} style={{ display:'block', overflow:'visible' }}>
          {node.content && (
            <path d={node.content} stroke={node.color||'#314F5C'} strokeWidth="2.5"
              fill="none" strokeLinecap="round" strokeLinejoin="round"
              opacity={selected ? 1 : 0.75}
              style={{ filter: selected ? 'drop-shadow(0 0 4px rgba(49,79,92,0.4))' : 'none' }}/>
          )}
        </svg>
        {showControls && (
          <div style={{ position:'absolute', top:-10, right:-10 }}>
            <DelBtn onDelete={onDelete}/>
          </div>
        )}
      </div>
    );
  }

  // ── Link card ──────────────────────────────────────────────────────────────
  if (isLink) {
    let url='', title='', favicon='';
    try { const p=JSON.parse(node.content||'{}'); url=p.url||''; title=p.title||url; favicon=p.favicon||''; } catch(_e){url=node.content||'';title=url;}
    return (
      <div style={{ position:'absolute', left:node.x, top:node.y, width:node.w, height:node.h,
        borderRadius:12, background:t.panel, border:`1px solid ${selected?'#314F5C':'rgba(49,79,92,0.12)'}`,
        boxShadow:selected?'0 0 0 2px #314F5C, 0 6px 20px rgba(15,30,40,0.12)':'0 2px 10px rgba(15,30,40,0.08)',
        display:'flex', flexDirection:'column', overflow:'hidden', userSelect:'none', cursor:'default' }}
        onMouseEnter={()=>setHovered(true)} onMouseLeave={()=>setHovered(false)}
        onPointerDown={e=>{if(!isLocked){if(e.currentTarget.setPointerCapture)e.currentTarget.setPointerCapture(e.pointerId);onDragStart(e);}}}
        onClick={onSelect} onDoubleClick={e=>{e.stopPropagation();if(url)window.open(url,'_blank');}}
        onContextMenu={e=>{e.preventDefault();onContextMenu&&onContextMenu(e);}}>
        <div style={{ height:22, background:'rgba(111,143,167,0.1)', borderBottom:'1px solid rgba(111,143,167,0.15)',
          display:'flex', alignItems:'center', padding:'0 8px', gap:6, flexShrink:0, cursor:isLocked?'default':'grab' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,3px)', gap:'2px', opacity:.3 }}>{[0,1,2,3,4,5].map(i=><div key={i} style={{ width:3, height:3, borderRadius:'50%', background:t.accent }}/>)}</div>
          <span style={{ fontSize:9, fontWeight:700, color:t.mut, opacity:.7, textTransform:'uppercase', letterSpacing:'.06em' }}>Link</span>
          {isLocked&&<svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#9eb8c8" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
          <div style={{ flex:1 }}/>
          {showControls&&<DelBtn onDelete={onDelete}/>}
        </div>
        <div style={{ flex:1, padding:'10px 12px', display:'flex', flexDirection:'column', gap:5, justifyContent:'center' }}>
          <div style={{ display:'flex', alignItems:'center', gap:7 }}>
            {favicon?<img src={favicon} width={14} height={14} style={{ borderRadius:2, flexShrink:0 }} onError={e=>e.target.style.display='none'}/>
              :<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6F8FA7" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>}
            <span style={{ fontSize:12.5, fontWeight:600, color:t.tx, lineHeight:1.3, wordBreak:'break-word' }}>{title||'Untitled link'}</span>
          </div>
          {url&&<span style={{ fontSize:10.5, color:t.mut, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{url.replace(/^https?:\/\//,'').slice(0,38)}</span>}
          <span style={{ fontSize:10, color:'#AFE1F6', marginTop:1 }}>Double-click to open ↗</span>
        </div>
      </div>
    );
  }

  // ── Cards (note / heading / checklist / image) ───────────────────────────────
  const items = isChecklist ? (Array.isArray(node.checked_items) ? node.checked_items : []) : [];

  return (
    <div
      style={{
        position:'absolute', left:node.x, top:node.y, width:node.w, height:node.h,
        borderRadius:12,
        boxShadow: selected ? '0 0 0 2.5px #314F5C, 0 8px 32px rgba(15,30,40,0.18)'
          : hovered ? '0 6px 24px rgba(15,30,40,0.14)' : '0 2px 10px rgba(15,30,40,0.09)',
        background: isImage ? '#fff' : colorObj.bg,
        border: connectMode ? `2px solid ${hovered ? '#314F5C' : 'rgba(49,79,92,0.18)'}`
          : isImage ? '1px solid rgba(49,79,92,0.1)' : `1px solid ${colorObj.accent}44`,
        display:'flex', flexDirection:'column', overflow:'hidden',
        transition:'box-shadow .15s, border-color .1s',
        userSelect: 'none',
        cursor: connectMode ? 'crosshair' : editing ? 'text' : 'grab',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onPointerDown={e => { if (connectMode||isLocked) return; if(e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId); onDragStart(e); }}
      onClick={e => { if (connectMode) { e.stopPropagation(); onConnectClick(); return; } onSelect(); }}
      onContextMenu={e => { e.preventDefault(); onContextMenu && onContextMenu(e); }}
      onDoubleClick={e => { if (connectMode||isLocked) return; e.stopPropagation(); onSelect(); onEdit(); }}
      title={node.updated_at ? 'Edited ' + new Date(node.updated_at).toLocaleString([], {dateStyle:'short',timeStyle:'short'}) : ''}>

      {/* Drag strip */}
      <div
        onPointerDown={e => { e.stopPropagation(); if (connectMode) return; if(e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId); onDragStart(e); }}
        style={{ height: isImage ? 28 : 24, background: isImage ? 'rgba(49,79,92,0.04)' : colorObj.accent+'44',
          borderBottom:'1px solid ' + colorObj.accent + '33',
          cursor: connectMode ? 'crosshair' : 'grab',
          display:'flex', alignItems:'center', padding:'0 8px', gap:6, flexShrink:0 }}>
        {/* 6-dot grip icon */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,3px)', gap:'2px', opacity:.35 }}>
          {[0,1,2,3,4,5].map(i => <div key={i} style={{ width:3, height:3, borderRadius:'50%', background:t.accent }}/>)}
        </div>
        {isLocked && <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={colorObj.text} strokeWidth="2.5" strokeLinecap="round" style={{ opacity:.5 }}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
        {isHeading && <div style={{ fontSize:9.5, fontWeight:700, color:colorObj.text, opacity:.6, letterSpacing:'.06em', textTransform:'uppercase', marginLeft:2 }}>Heading</div>}
        {isChecklist && <div style={{ fontSize:9.5, fontWeight:700, color:colorObj.text, opacity:.6, letterSpacing:'.06em', textTransform:'uppercase', marginLeft:2 }}>Checklist{items.length>0 ? ` · ${items.filter(i=>i.done).length}/${items.length}` : ''}</div>}
        <div style={{ flex:1 }}/>
        {showControls && <DelBtn onDelete={onDelete}/>}
        {showControls && onToggleLock && (
          <button onPointerDown={e=>{e.stopPropagation();onToggleLock();}}
            title={isLocked?'Unlock':'Lock node'}
            style={{ width:16, height:16, borderRadius:4, border:'none', background:isLocked?'rgba(49,79,92,0.18)':'rgba(49,79,92,0.08)', color:isLocked?'#314F5C':'rgba(49,79,92,0.4)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, flexShrink:0 }}>
            <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/>{isLocked?<path d="M7 11V7a5 5 0 0 1 10 0v4"/>:<path d="M7 11V7a5 5 0 0 1 9.9-1"/>}</svg>
          </button>
        )}
      </div>

      {/* Body */}
      {isImage ? (
        <div style={{ flex:1, position:'relative', overflow:'hidden' }}>
          <img src={node.image_url} alt="" style={{ width:'100%', height: node.content ? 'calc(100% - 38px)' : '100%', objectFit:'cover', display:'block' }} draggable={false}/>
          {(editing || node.content) && (
            <div style={{ padding:'5px 8px', borderTop:'1px solid rgba(49,79,92,0.1)', background:'#fafbfc' }}>
              {editing
                ? <textarea autoFocus value={node.content} onChange={e => onContentChange(e.target.value)} onBlur={onBlur} placeholder="Add annotation…" style={{ width:'100%', border:'none', outline:'none', resize:'none', fontFamily:F, fontSize:11, color:t.accent, background:'transparent', minHeight:28 }}/>
                : <p style={{ margin:0, fontSize:11, color:t.accent, fontFamily:F, lineHeight:1.5 }}>{node.content}</p>}
            </div>
          )}
        </div>
      ) : isHeading ? (
        <div style={{ flex:1, padding:'10px 12px', overflow:'hidden', display:'flex', flexDirection:'column', justifyContent:'center' }}>
          {editing ? (
            <>
              <input autoFocus value={node.content} onChange={e => onContentChange(e.target.value)}
                placeholder="Title…"
                style={{ border:'none', outline:'none', fontFamily:F, fontSize:17, fontWeight:700, color:colorObj.text, background:'transparent', marginBottom:4, width:'100%' }}/>
              <input value={node.subtitle||''} onChange={e => onSubtitleChange(e.target.value)} onBlur={onBlur}
                placeholder="Subtitle…"
                style={{ border:'none', outline:'none', fontFamily:F, fontSize:12, color:colorObj.text, background:'transparent', opacity:.55, width:'100%' }}/>
            </>
          ) : (
            <>
              <div style={{ fontSize:17, fontWeight:700, color:colorObj.text, fontFamily:F, lineHeight:1.2, marginBottom: node.subtitle ? 4 : 0, wordBreak:'break-word' }}>{node.content||<span style={{ opacity:.3 }}>Title</span>}</div>
              {node.subtitle && <div style={{ fontSize:12, color:colorObj.text, opacity:.55, fontFamily:F, lineHeight:1.4 }}>{node.subtitle}</div>}
            </>
          )}
        </div>
      ) : isChecklist ? (
        <div style={{ flex:1, padding:'8px 10px', overflowY:'auto' }}>
          {items.map((item, idx) => (
            <div key={idx} style={{ display:'flex', alignItems:'flex-start', gap:7, marginBottom:5 }}>
              <div onClick={e => { e.stopPropagation(); onCheckToggle(idx); }}
                style={{ width:14, height:14, borderRadius:4, border:`1.5px solid ${item.done ? '#314F5C' : colorObj.accent}`,
                  background: item.done ? '#314F5C' : 'transparent', flexShrink:0, cursor:'pointer',
                  display:'flex', alignItems:'center', justifyContent:'center', marginTop:2 }}>
                {item.done && <svg width="8" height="8" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><polyline points="1,6 5,10 11,2"/></svg>}
              </div>
              {(editing && editingCheckIdx === idx) ? (
                <input autoFocus value={item.text}
                  onChange={e => onContentChange(items.map((it,i) => i===idx ? {...it,text:e.target.value} : it))}
                  onKeyDown={e => {
                    if (e.key==='Enter') { e.preventDefault(); const next=[...items,{text:'',done:false}]; onContentChange(next); onEditCheckIdx(next.length-1); }
                    if (e.key==='Backspace' && item.text==='' && items.length>1) { e.preventDefault(); onContentChange(items.filter((_,i)=>i!==idx)); onEditCheckIdx(Math.max(0,idx-1)); }
                  }}
                  onBlur={onBlur}
                  style={{ flex:1, border:'none', outline:'none', fontFamily:F, fontSize:12.5, color: item.done ? colorObj.text+'66' : colorObj.text, background:'transparent', textDecoration: item.done ? 'line-through' : 'none' }}/>
              ) : (
                <span style={{ flex:1, fontSize:12.5, color: item.done ? colorObj.text+'55' : colorObj.text, fontFamily:F, textDecoration: item.done ? 'line-through' : 'none', lineHeight:1.5, wordBreak:'break-word', cursor:'text' }}
                  onClick={e => { e.stopPropagation(); onSelect(); onEditCheckIdx(idx); }}>
                  {item.text || <span style={{ opacity:.3 }}>Item {idx+1}</span>}
                </span>
              )}
            </div>
          ))}
          {(hovered || selected) && (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:3 }}>
              <button onClick={e => { e.stopPropagation(); onAddCheckItem(); }}
                style={{ fontSize:11, color:colorObj.accent, background:'none', border:'none', cursor:'pointer', fontFamily:F, padding:0, display:'flex', alignItems:'center', gap:4, opacity:.7 }}
                onMouseEnter={e => e.currentTarget.style.opacity='1'}
                onMouseLeave={e => e.currentTarget.style.opacity='.7'}>
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="6" y1="1" x2="6" y2="11"/><line x1="1" y1="6" x2="11" y2="6"/></svg>
                Add item
              </button>
              {onPushToPlanner && items.some(it => it.text && !it.done) && (
                <button onClick={e => { e.stopPropagation(); onPushToPlanner(); }}
                  title="Push uncompleted items to Planner as tasks"
                  style={{ fontSize:10, color:colorObj.accent, background:'none', border:'none', cursor:'pointer', fontFamily:F, padding:'2px 6px', borderRadius:5, opacity:.65, display:'flex', alignItems:'center', gap:3 }}
                  onMouseEnter={e => { e.currentTarget.style.opacity='1'; e.currentTarget.style.background='rgba(49,79,92,0.07)'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity='.65'; e.currentTarget.style.background='none'; }}>
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  Tasks
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div style={{ flex:1, padding:'10px 12px', overflow:'hidden' }}>
          {editing
            ? <textarea autoFocus value={node.content} onChange={e => onContentChange(e.target.value)} onBlur={onBlur} placeholder="Type your note…" style={{ width:'100%', height:'100%', border:'none', outline:'none', resize:'none', fontFamily:F, fontSize:13, color:colorObj.text, background:'transparent', lineHeight:1.6 }}/>
            : <div style={{ margin:0, fontSize:13, color:colorObj.text, fontFamily:F, lineHeight:1.6, wordBreak:'break-word', opacity: node.content ? 1 : 0.45 }}>{node.content ? renderMarkdown(node.content) : <span>Double-click to edit</span>}</div>}
        </div>
      )}

      {/* Color picker is now floating — rendered outside the card in Studio */}

      <ResizeHandle visible={showControls} onMouseDown={e => { e.stopPropagation(); onResizeStart(e, node); }}/>
    </div>
  );
}

// ── Markdown renderer ───────────────────────────────────────────────────────
function renderMarkdown(text) {
  if (!text) return null;
  return text.split('\n').map((line, li) => {
    if (line.startsWith('- ') || line.startsWith('• ')) {
      return <div key={li} style={{ display:'flex', gap:5, marginBottom:2 }}><span style={{ flexShrink:0 }}>•</span><span>{fmtInline(line.slice(2))}</span></div>;
    }
    return <div key={li} style={{ marginBottom:1 }}>{fmtInline(line)}</div>;
  });
}
function fmtInline(text) {
  const parts = []; let i=0, k=0;
  while (i < text.length) {
    if (text[i]==='*'&&text[i+1]==='*') { const e=text.indexOf('**',i+2); if(e>i+1){parts.push(<strong key={k++}>{text.slice(i+2,e)}</strong>);i=e+2;continue;} }
    if (text[i]==='_') { const e=text.indexOf('_',i+1); if(e>i){parts.push(<em key={k++}>{text.slice(i+1,e)}</em>);i=e+1;continue;} }
    let j=i+1; while(j<text.length&&text[j]!=='*'&&text[j]!=='_')j++;
    parts.push(<span key={k++}>{text.slice(i,j)}</span>); i=j;
  }
  return parts;
}

// ── Shortcuts modal ──────────────────────────────────────────────────────────
function ShortcutsModal({ onClose }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const groups = [
    { g:'Canvas', items:[['F','Fit all to screen'],['Dbl-click canvas','Add note at cursor'],['Scroll','Zoom'],['Drag background','Pan']] },
    { g:'Nodes', items:[['Click','Select'],['Shift+Click','Multi-select'],['Dbl-click','Edit'],['Drag','Move'],['Shift+Drag','Snap to grid'],['Right-click','Context menu']] },
    { g:'Editing', items:[['⌘D','Duplicate'],['  ⌘Z','Undo'],['Del','Delete selected'],['Esc','Cancel / deselect']] },
    { g:'Tools', items:[['⌘F','Search board'],['?','Shortcuts'],['Sana button','AI assistant']] },
    { g:'Markdown', items:[['**text**','Bold'],['_text_','Italic'],['- text','Bullet']] },
  ];
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,30,40,0.55)', zIndex:10100, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:t.panel, borderRadius:16, width:'100%', maxWidth:500, boxShadow:'0 24px 64px rgba(0,0,0,0.25)', overflow:'hidden', fontFamily:F }}>
        <div style={{ padding:'18px 22px 14px', borderBottom:`1px solid ${t.bd}`, display:'flex', alignItems:'center' }}>
          <span style={{ flex:1, fontSize:14, fontWeight:700, color:t.tx }}>Keyboard Shortcuts</span>
          <button onClick={onClose} style={{ width:26, height:26, borderRadius:6, border:'none', background:'rgba(49,79,92,0.07)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:t.mut }}><svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/></svg></button>
        </div>
        <div style={{ padding:'16px 22px 20px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 24px', maxHeight:'65vh', overflowY:'auto' }}>
          {groups.map(({ g, items }) => (
            <div key={g} style={{ marginBottom:14 }}>
              <div style={{ fontSize:10, fontWeight:700, color:t.mut, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>{g}</div>
              {items.map(([key, desc]) => (
                <div key={key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:5 }}>
                  <span style={{ fontSize:11.5, color:t.mut }}>{desc}</span>
                  <kbd style={{ fontSize:10, fontWeight:600, color:t.accent, background:'rgba(49,79,92,0.07)', border:'1px solid rgba(49,79,92,0.15)', borderRadius:5, padding:'2px 6px', whiteSpace:'nowrap', flexShrink:0 }}>{key}</kbd>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Template picker ──────────────────────────────────────────────────────────
function TemplateModal({ onApply, onSkip }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,30,40,0.5)', zIndex:10100, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ background:t.panel, borderRadius:16, width:'100%', maxWidth:520, boxShadow:'0 24px 64px rgba(0,0,0,0.22)', overflow:'hidden', fontFamily:F }}>
        <div style={{ padding:'20px 24px 14px', borderBottom:`1px solid ${t.bd}` }}>
          <div style={{ fontSize:14, fontWeight:700, color:t.tx, marginBottom:3 }}>Start with a template</div>
          <div style={{ fontSize:12, color:t.mut }}>Or start blank and build your own</div>
        </div>
        <div style={{ padding:'16px 24px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:14 }}>
            {TEMPLATES.map(t => (
              <button key={t.id} onClick={()=>onApply(t)}
                style={{ padding:'14px 12px', borderRadius:11, border:'1.5px solid #dde8ed', background:t.rowbg, cursor:'pointer', textAlign:'left', transition:'all .12s' }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor=t.color;e.currentTarget.style.background='#f4f7f9';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='#dde8ed';e.currentTarget.style.background='#fafcfd';}}>
                <div style={{ fontSize:22, marginBottom:6 }}>{t.emoji}</div>
                <div style={{ fontSize:12, fontWeight:700, color:t.tx, marginBottom:3 }}>{t.name}</div>
                <div style={{ fontSize:11, color:t.mut, lineHeight:1.4 }}>{t.desc}</div>
              </button>
            ))}
          </div>
          <button onClick={onSkip}
            style={{ width:'100%', padding:'9px', border:'1.5px dashed #d0dce4', borderRadius:9, background:'none', cursor:'pointer', fontSize:12, color:t.mut, fontFamily:F }}>Start with blank board</button>
        </div>
      </div>
    </div>
  );
}

// ── Search bar ───────────────────────────────────────────────────────────────
function SearchBar({ query, onChange, onClose, resultCount, onNext, onPrev, current }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const ref = React.useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  const F2 = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
  return (
    <div style={{ position:'absolute', top:12, right:14, zIndex:500, background:t.panel, borderRadius:10,
      padding:'6px 10px', display:'flex', alignItems:'center', gap:8,
      boxShadow:'0 4px 20px rgba(15,30,40,0.18), 0 0 0 1px rgba(15,30,40,0.07)', fontFamily:F2, minWidth:260 }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9eb8c8" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
      <input ref={ref} value={query} onChange={e=>onChange(e.target.value)}
        placeholder="Search notes…"
        onKeyDown={e=>{if(e.key==='Escape')onClose();if(e.key==='Enter'){e.shiftKey?onPrev():onNext();}}}
        style={{ flex:1, border:'none', outline:'none', fontSize:12.5, color:t.tx, fontFamily:F2, background:'transparent' }}/>
      {query && <span style={{ fontSize:11, color:t.mut, whiteSpace:'nowrap' }}>{resultCount===0?'No results':`${current+1} / ${resultCount}`}</span>}
      {resultCount > 0 && (
        <>
          <button onClick={onPrev} style={{ width:22, height:22, borderRadius:5, border:'1px solid #e4edf3', background:t.rowbg, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#6F8FA7" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg></button>
          <button onClick={onNext} style={{ width:22, height:22, borderRadius:5, border:'1px solid #e4edf3', background:t.rowbg, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}><svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#6F8FA7" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg></button>
        </>
      )}
      <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:t.mut, fontSize:16, lineHeight:1, padding:0, marginLeft:2 }}>x</button>
    </div>
  );
}

// ── Minimap ──────────────────────────────────────────────────────────────────
function Minimap({ nodes, panX, panY, zoom, canvasW, canvasH, onJump }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const W=140, H=88;
  if (!nodes.length) return null;
  const minX=Math.min(...nodes.map(n=>n.x))-20, minY=Math.min(...nodes.map(n=>n.y))-20;
  const maxX=Math.max(...nodes.map(n=>n.x+n.w))+20, maxY=Math.max(...nodes.map(n=>n.y+n.h))+20;
  const bw=maxX-minX||1, bh=maxY-minY||1;
  const sc=Math.min(W/bw, H/bh, 0.3);
  const offX=(W-bw*sc)/2, offY=(H-bh*sc)/2;
  const vpX=(-panX)/zoom, vpY=(-panY)/zoom, vpW=canvasW/zoom, vpH=canvasH/zoom;
  const rx=offX+(vpX-minX)*sc, ry=offY+(vpY-minY)*sc, rw=vpW*sc, rh=vpH*sc;
  return (
    <div style={{ position:'absolute', bottom:60, left:14, zIndex:90, background:'rgba(255,255,255,0.93)', borderRadius:9, padding:6,
      boxShadow:'0 2px 12px rgba(15,30,40,0.14), 0 0 0 1px rgba(15,30,40,0.07)', cursor:'crosshair' }}
      onClick={e => { const r=e.currentTarget.getBoundingClientRect(); const mx=e.clientX-r.left, my=e.clientY-r.top; onJump((mx-offX)/sc+minX,(my-offY)/sc+minY); }}>
      <svg width={W} height={H}>
        {nodes.map(n => {
          const nx=offX+(n.x-minX)*sc, ny=offY+(n.y-minY)*sc, nw=Math.max(n.w*sc,4), nh=Math.max(n.h*sc,3);
          const nt=n.node_type||n.type||'note';
          if(nt==='section') return <rect key={n.id} x={nx} y={ny} width={nw} height={nh} rx={2} fill="none" stroke="rgba(49,79,92,0.25)" strokeWidth="0.8" strokeDasharray="2 1"/>;
          if(nt==='drawing') return <rect key={n.id} x={nx} y={ny} width={nw} height={nh} rx={1} fill="rgba(49,79,92,0.3)"/>;
          return <rect key={n.id} x={nx} y={ny} width={nw} height={nh} rx={2} fill={n.color||'#E0F5FC'} stroke="rgba(49,79,92,0.1)" strokeWidth="0.5"/>;
        })}
        <rect x={Math.max(0,rx)} y={Math.max(0,ry)} width={Math.min(rw,W-Math.max(0,rx))} height={Math.min(rh,H-Math.max(0,ry))} fill="rgba(49,79,92,0.06)" stroke="#314F5C" strokeWidth="1.2" rx={2}/>
      </svg>
      <div style={{ fontSize:8, color:'rgba(49,79,92,0.3)', textAlign:'center', marginTop:2, fontFamily:F, fontWeight:600, letterSpacing:'.04em', textTransform:'uppercase' }}>Minimap</div>
    </div>
  );
}

// ── Sana AI panel ────────────────────────────────────────────────────────────
function SanaPanel({ boardName, nodes, onClose, onDropToCanvas }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [input, setInput] = React.useState('');
  const [msgs, setMsgs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const btmRef = React.useRef(null);
  const taRef = React.useRef(null);

  useEffect(() => { btmRef.current?.scrollIntoView({ behavior:'smooth' }); }, [msgs]);

  // ── Sana avatar — matches app exactly ─────────────────────────────────────
  const SanaAvatar = ({ size=36 }) => (
    <div style={{ width:size, height:size, borderRadius:'50%', background:'#AFE1F6',
      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      {/* Sanoire pin / fingerprint icon */}
      <svg width={size*0.44} height={size*0.44} viewBox="0 0 20 24" fill="none">
        <path d="M10 0C4.48 0 0 4.48 0 10c0 7.5 10 14 10 14s10-6.5 10-14c0-5.52-4.48-10-10-10z"
          fill="#314F5C"/>
        <circle cx="10" cy="10" r="3.5" fill="#AFE1F6"/>
      </svg>
    </div>
  );

  async function send(overrideText) {
    const text = (overrideText || input).trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role:'user', content:text };
    setMsgs(prev => [...prev, userMsg]);
    setLoading(true);
    try {
      const ctx = nodes.slice(0,20).map(n => {
        const nt = n.node_type||n.type||'note';
        if (nt==='checklist') return '[Checklist] ' + (Array.isArray(n.checked_items) ? n.checked_items.map(i=>i.text).join(', ') : '');
        return '[' + nt + '] ' + (n.content||'').slice(0,80);
      }).filter(Boolean).join('\n');
      const sys = 'You are Sana, the AI assistant for Sanoire — a global healthcare & wellness travel platform. The user is working in Studio, a visual planning canvas. Current board: "' + boardName + '". Board contents:\n' + (ctx || '(empty board)') + '\n\nBe concise and practical. Max 3 sentences or 5 bullet points.';
      const history = msgs.map(m => ({ role:m.role, content:m.content }));
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ model:'claude-sonnet-4-20250514', max_tokens:500, system:sys, messages:[...history, userMsg] }),
      });
      const d = await res.json();
      setMsgs(prev => [...prev, { role:'assistant', content: d?.content?.[0]?.text || 'Sorry, try again.' }]);
    } catch(_e) {
      setMsgs(prev => [...prev, { role:'assistant', content: 'Could not reach Sana. Check your connection.' }]);
    }
    setLoading(false);
  }

  function clearChat() { setMsgs([]); setInput(''); }

  const PROMPTS = [
    'Summarise this board in 3 bullets',
    'What tasks might be missing?',
    'Suggest copy for a campaign',
    'What are next steps?',
  ];

  // ── Design tokens matching the app ────────────────────────────────────────
  const panelBg = '#F5F9FB';
  const dividerColor = 'rgba(49,79,92,0.08)';
  const primaryText = '#1d3540';
  const subtleText = '#6F8FA7';

  return (
    <div style={{ width:300, flexShrink:0, background:panelBg,
      display:'flex', flexDirection:'column',
      borderLeft:'1px solid ' + dividerColor, fontFamily:F, overflow:'hidden' }}>

      {/* ── Header ── */}
      <div style={{ height:56, padding:'0 14px', background:t.panel,
        borderBottom:'1px solid ' + dividerColor,
        display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
        <SanaAvatar size={34}/>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:700, color:primaryText, lineHeight:1.2 }}>Sana</div>
          <div style={{ fontSize:11, color:subtleText, lineHeight:1.2 }}>Board assistant</div>
        </div>
        {msgs.length > 0 && (
          <button onClick={clearChat}
            style={{ height:26, padding:'0 10px', borderRadius:999, border:'0.67px solid rgba(49,79,92,0.18)',
              background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:5,
              fontSize:11.5, color:subtleText, fontFamily:F, transition:'all .12s' }}
            onMouseEnter={e=>{e.currentTarget.style.background='rgba(49,79,92,0.05)';}}
            onMouseLeave={e=>{e.currentTarget.style.background='transparent';}}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
              <path d="M3 3v5h5"/>
            </svg>
            New chat
          </button>
        )}
        <button onClick={onClose}
          style={{ width:26, height:26, borderRadius:999, border:'0.67px solid rgba(49,79,92,0.15)',
            background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
            color:subtleText, flexShrink:0, transition:'all .12s' }}
          onMouseEnter={e=>{e.currentTarget.style.background='rgba(49,79,92,0.05)';}}
          onMouseLeave={e=>{e.currentTarget.style.background='transparent';}}>
          <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/>
          </svg>
        </button>
      </div>

      {/* ── Messages ── */}
      <div style={{ flex:1, overflowY:'auto', padding:'14px 14px 8px',
        display:'flex', flexDirection:'column', gap:12 }}>

        {/* Empty state */}
        {msgs.length === 0 && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-start', gap:12 }}>
            {/* Sana greeting bubble */}
            <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
              <SanaAvatar size={28}/>
              <div style={{ background:t.panel, borderRadius:'12px 12px 12px 4px',
                padding:'10px 14px', fontSize:13, color:primaryText, lineHeight:1.6,
                boxShadow:'0 1px 4px rgba(15,30,40,0.06)', maxWidth:210,
                border:'1px solid rgba(49,79,92,0.07)' }}>
                I’m Sana. Ask me anything about this board, or try a suggestion:
              </div>
            </div>
            {/* Suggestion chips — exact app style */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, paddingLeft:36 }}>
              {PROMPTS.map(p => (
                <button key={p.text}
                  onClick={() => send(p.text)}
                  style={{ padding:'5px 12px', borderRadius:999,
                    border:'0.67px solid rgba(49,79,92,0.2)',
                    background:'rgba(49,79,92,0.05)',
                    color:t.accent, fontSize:12, cursor:'pointer',
                    fontFamily:F, lineHeight:1.4, transition:'all .12s' }}
                  onMouseEnter={e=>{e.currentTarget.style.background='rgba(49,79,92,0.1)';e.currentTarget.style.borderColor='rgba(49,79,92,0.35)';}}
                  onMouseLeave={e=>{e.currentTarget.style.background='rgba(49,79,92,0.05)';e.currentTarget.style.borderColor='rgba(49,79,92,0.2)';}}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Message thread */}
        {msgs.map((m, i) => (
          <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start',
            flexDirection: m.role==='user' ? 'row-reverse' : 'row' }}>
            {m.role === 'assistant' && <SanaAvatar size={26}/>}
            {m.role === 'user' && (
              <div style={{ width:26, height:26, borderRadius:'50%', background:t.accent,
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#AFE1F6" strokeWidth="2" strokeLinecap="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
            )}
            <div style={{ display:'flex', flexDirection:'column', gap:3, maxWidth:'78%',
              alignItems: m.role==='user' ? 'flex-end' : 'flex-start' }}>
              <div style={{
                padding:'10px 14px', lineHeight:1.6, wordBreak:'break-word', whiteSpace:'pre-wrap',
                fontSize:13, borderRadius: m.role==='user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                background: m.role==='user' ? '#314F5C' : '#fff',
                color: m.role==='user' ? '#E0F5FC' : primaryText,
                boxShadow: '0 1px 4px rgba(15,30,40,0.06)',
                border: m.role==='user' ? 'none' : '1px solid rgba(49,79,92,0.07)',
              }}>
                {m.content}
              </div>
              {m.role === 'assistant' && (
                <button onClick={() => onDropToCanvas(m.content)}
                  style={{ fontSize:11, color:t.accent, background:'rgba(49,79,92,0.06)', border:'1px solid rgba(49,79,92,0.15)',
                    cursor:'pointer', fontFamily:F, padding:'4px 10px', borderRadius:6,
                    display:'flex', alignItems:'center', gap:5, transition:'all .12s', marginTop:2 }}
                  onMouseEnter={e=>{e.currentTarget.style.background='rgba(49,79,92,0.12)';e.currentTarget.style.borderColor='rgba(49,79,92,0.3)';}}
                  onMouseLeave={e=>{e.currentTarget.style.background='rgba(49,79,92,0.06)';e.currentTarget.style.borderColor='rgba(49,79,92,0.15)';}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add to board
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Typing indicator */}
        {loading && (
          <div style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
            <SanaAvatar size={26}/>
            <div style={{ background:t.panel, borderRadius:'12px 12px 12px 4px',
              padding:'12px 16px', border:'1px solid rgba(49,79,92,0.07)',
              boxShadow:'0 1px 4px rgba(15,30,40,0.06)', display:'flex', gap:5, alignItems:'center' }}>
              {[0,1,2].map(i => (
                <div key={i} style={{ width:6, height:6, borderRadius:'50%', background:'#AFE1F6',
                  animation:`studioPulse 1.1s ease-in-out ${i*0.18}s infinite` }}/>
              ))}
            </div>
          </div>
        )}
        <div ref={btmRef}/>
      </div>

      {/* ── Input ── */}
      <div style={{ padding:'10px 14px 12px', background:t.panel,
        borderTop:'1px solid ' + dividerColor, flexShrink:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:0,
          background:'#F0F5F7', borderRadius:14, border:'1px solid rgba(49,79,92,0.1)',
          padding:'0 6px 0 14px', transition:'border-color .12s' }}
          onFocusCapture={e=>e.currentTarget.style.borderColor='rgba(49,79,92,0.25)'}
          onBlurCapture={e=>e.currentTarget.style.borderColor='rgba(49,79,92,0.1)'}>
          <textarea
            ref={taRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if(e.key==='Enter'&&!e.shiftKey){ e.preventDefault(); send(); }}}
            placeholder="Ask Sana anything…"
            rows={1}
            style={{ flex:1, background:'transparent', border:'none', outline:'none',
              fontSize:13.5, color:t.accent, fontFamily:F, resize:'none',
              lineHeight:1.5, padding:'10px 0', minHeight:0, maxHeight:80, overflowY:'auto' }}/>
          <button
            onClick={() => send()}
            disabled={!input.trim() || loading}
            style={{ width:34, height:34, borderRadius:'50%', border:'none', flexShrink:0,
              background: input.trim() && !loading ? '#314F5C' : '#AFE1F6',
              cursor: input.trim() && !loading ? 'pointer' : 'default',
              display:'flex', alignItems:'center', justifyContent:'center',
              transition:'background .15s' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke={input.trim() && !loading ? '#E0F5FC' : 'rgba(49,79,92,0.45)'}
              strokeWidth="2.5" strokeLinecap="round">
              <path d="M22 2L11 13"/><path d="M22 2L15 22 11 13 2 9l20-7z"/>
            </svg>
          </button>
        </div>
        <div style={{ fontSize:9.5, color:'rgba(49,79,92,0.3)', marginTop:6, textAlign:'center', fontFamily:F }}>
          Enter to send· Shift+Enter for new line
        </div>
      </div>
    </div>
  );
}

// ── Context menu ─────────────────────────────────────────────────────────────
function ContextMenu({ x, y, onDuplicate, onDelete, onBringToFront, onSendToBack, onToggleLock, isLocked, onLinkToBug, onPushToPlanner, onClose }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const ref = useRef(null);
  useEffect(() => {
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    setTimeout(() => document.addEventListener('pointerdown', onDown, true), 0);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [onClose]);
  const items = [
    { label:'Duplicate', hint:'⌘D', fn:onDuplicate },
    { label:'Bring to front', fn:onBringToFront },
    { label:'Send to back', fn:onSendToBack },
    { label: isLocked ? 'Unlock node' : 'Lock node', fn:onToggleLock },
    { sep:true },
    { label:'🐛 Link to App bug', fn:onLinkToBug, muted:true },
    { label:'📋 Push to Planner', fn:onPushToPlanner, muted:true },
    { sep:true },
    { label:'Delete', hint:'⌫', fn:onDelete, danger:true },
  ];
  return (
    <div ref={ref} style={{ position:'fixed', left:x, top:y, zIndex:10000,
      background:t.panel, borderRadius:10, padding:'4px 0', minWidth:164,
      boxShadow:'0 8px 32px rgba(15,30,40,0.18), 0 0 0 1px rgba(15,30,40,0.07)',
      fontFamily:F }}>
      {items.map((item, i) => {
        if (item.sep) return <div key={i} style={{ height:1, background:'#edf0f3', margin:'3px 0' }}/>;
        return (
          <button key={i}
            onPointerDown={e => { e.stopPropagation(); if (item.fn) { item.fn(); onClose(); } }}
            style={{ width:'100%', display:'flex', alignItems:'center', gap:8, padding:'7px 14px',
              border:'none', background:'transparent', cursor:'pointer', fontFamily:F,
              fontSize:12.5, fontWeight:500, color:item.danger?'#c62828':item.muted?'#6F8FA7':'#0f1e28', textAlign:'left' }}
            onMouseEnter={e => e.currentTarget.style.background=item.danger?'#fef2f2':'#f4f7f9'}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
            <span style={{ flex:1 }}>{item.label}</span>
            {item.hint && <span style={{ fontSize:11, color:'#b0cad5' }}>{item.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── Floating color picker ────────────────────────────────────────────────────
function FloatingColorPicker({ node, panX, panY, zoom, onColorChange }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const screenX = panX + (node.x + node.w / 2) * zoom;
  const screenY = panY + (node.y + node.h) * zoom + 10;
  return (
    <div style={{ position:'absolute', left:screenX, top:screenY, transform:'translateX(-50%)',
      background:t.panel, borderRadius:99, padding:'6px 10px', zIndex:200,
      boxShadow:'0 4px 20px rgba(15,30,40,0.15), 0 0 0 1px rgba(15,30,40,0.06)',
      display:'flex', alignItems:'center', gap:7, pointerEvents:'all' }}>
      {NOTE_COLORS.map(c => (
        <button key={c.id}
          onPointerDown={e => { e.stopPropagation(); onColorChange(c.bg); }}
          style={{ width:17, height:17, borderRadius:'50%', background:c.bg,
            border: node.color===c.bg ? '2.5px solid #314F5C' : '1.5px solid ' + c.accent,
            cursor:'pointer', padding:0, flexShrink:0,
            transform: node.color===c.bg ? 'scale(1.3)' : 'scale(1)', transition:'transform .1s' }}/>
      ))}
    </div>
  );
}

// ── Boards sidebar (collapsible) ─────────────────────────────────────────────
function BoardsSidebar({ boards, activeBoardId, onSelect, onCreate, onRename, onDelete, open, onToggle, linkedTask, onOpenPlanner }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const ic = 'rgba(175,225,246,0.6)';

  if (!open) {
    return (
      <div style={{ width:52, background:'#0d1d2b', display:'flex', flexDirection:'column',
        flexShrink:0, height:'100%', overflow:'hidden', alignItems:'center' }}>
        <div style={{ paddingTop:10, paddingBottom:8, borderBottom:'1px solid rgba(175,225,246,0.07)',
          width:'100%', display:'flex', justifyContent:'center' }}>
          <button onClick={onToggle} title="Show boards"
            style={{ width:34, height:34, borderRadius:8, border:'none', background:'rgba(175,225,246,0.07)',
              cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:ic }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
              <rect x="14" y="14" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/>
            </svg>
          </button>
        </div>
        <div style={{ flex:1, overflowY:'auto', width:'100%', padding:'6px 0',
          display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
          {boards.map(board => {
            const active = board.id === activeBoardId;
            const bc = board.color || '#314F5C';
            return (
              <div key={board.id} onClick={() => onSelect(board.id)} title={board.name}
                style={{ width:34, height:34, borderRadius:8, cursor:'pointer', flexShrink:0,
                  background: active ? bc+'28' : 'transparent',
                  border: '1.5px solid ' + (active ? bc : 'transparent'),
                  display:'flex', alignItems:'center', justifyContent:'center', transition:'all .12s' }}>
                <div style={{ width:9, height:9, borderRadius:'50%', background:bc, opacity:active?1:0.5 }}/>
              </div>
            );
          })}
        </div>
        <div style={{ padding:'8px 0', borderTop:'1px solid rgba(175,225,246,0.07)',
          width:'100%', display:'flex', justifyContent:'center' }}>
          <button onClick={onCreate} title="New board"
            style={{ width:34, height:34, borderRadius:8, border:'1px solid rgba(175,225,246,0.12)',
              background:'rgba(175,225,246,0.05)', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center', color:ic }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width:200, background:'#0d1d2b', display:'flex', flexDirection:'column', flexShrink:0, height:'100%', overflow:'hidden' }}>
      <div style={{ padding:'10px 12px 10px', borderBottom:'1px solid rgba(175,225,246,0.08)', display:'flex', alignItems:'center', gap:8 }}>
        <button onClick={onToggle} title="Collapse"
          style={{ width:24, height:24, borderRadius:6, border:'none', background:'rgba(175,225,246,0.07)',
            cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:ic, flexShrink:0 }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <span style={{ fontSize:10, fontWeight:700, color:'rgba(175,225,246,0.55)', letterSpacing:'.07em', textTransform:'uppercase', fontFamily:F }}>Boards</span>
        <button onClick={onCreate} title="New board"
          style={{ marginLeft:'auto', width:24, height:24, borderRadius:6, border:'1px solid rgba(175,225,246,0.12)',
            background:'rgba(175,225,246,0.06)', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', color:ic }}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </button>
      </div>
      <div style={{ flex:1, overflowY:'auto', padding:'6px 8px' }}>
        {boards.map(board => {
          const active = board.id === activeBoardId;
          const bc = board.color || '#314F5C';
          return (
            <div key={board.id}
              style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 8px', borderRadius:7, marginBottom:2,
                background: active ? 'rgba(175,225,246,0.09)' : 'transparent',
                borderLeft: '2px solid ' + (active ? '#6FBFE0' : 'transparent'), cursor:'pointer' }}
              onClick={() => onSelect(board.id)}>
              <div style={{ width:20, height:20, borderRadius:5, background:bc+'22', border:'1.5px solid ' + bc + '55',
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <div style={{ width:7, height:7, borderRadius:'50%', background:bc, opacity:active?1:0.6 }}/>
              </div>
              {editingId === board.id ? (
                <input autoFocus value={editName}
                  onChange={e => setEditName(e.target.value)}
                  onBlur={() => { onRename(board.id, editName.trim()||board.name); setEditingId(null); }}
                  onKeyDown={e => { if (e.key==='Enter'||e.key==='Escape') { onRename(board.id, editName.trim()||board.name); setEditingId(null); }}}
                  style={{ flex:1, background:'transparent', border:'none', outline:'none', fontFamily:F, fontSize:11.5, color:'#e8f4fb', minWidth:0 }}
                  onClick={e => e.stopPropagation()}/>
              ) : (
                <span style={{ flex:1, fontSize:11.5, color: active?'#e8f4fb':'rgba(175,225,246,0.6)',
                  fontWeight:active?600:400, fontFamily:F, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                  onDoubleClick={e => { e.stopPropagation(); setEditingId(board.id); setEditName(board.name); }}>
                  {board.name}
                </span>
              )}
              {active && boards.length > 1 && (
                <button onClick={e => { e.stopPropagation(); if (window.confirm('Delete "' + board.name + '" and all its notes?')) onDelete(board.id); }}
                  style={{ width:14, height:14, borderRadius:3, border:'none', background:'rgba(198,40,40,0.15)',
                    color:'rgba(198,80,80,0.7)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', padding:0, flexShrink:0 }}>
                  <svg width="7" height="7" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/></svg>
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div style={{ padding:'8px 12px', borderTop:'1px solid rgba(175,225,246,0.07)' }}>
        <div style={{ fontSize:9.5, color:'rgba(175,225,246,0.25)', fontFamily:F }}>Double-click name to rename</div>
      </div>
      {/* Planner mini-panel */}
      {linkedTask && (
        <div style={{ padding:'10px 12px', borderTop:'1px solid rgba(175,225,246,0.08)', flexShrink:0 }}>
          <div style={{ fontSize:9, fontWeight:700, color:'rgba(175,225,246,0.4)', letterSpacing:'.07em', textTransform:'uppercase', marginBottom:6, fontFamily:F }}>Linked task</div>
          <div style={{ background:'rgba(175,225,246,0.06)', borderRadius:8, padding:'8px 10px' }}>
            <div style={{ fontSize:11.5, fontWeight:600, color:'#e8f4fb', lineHeight:1.4, marginBottom:5, fontFamily:F }}>{linkedTask.title}</div>
            <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:6 }}>
              {(() => {
                const sc = {todo:{bg:'rgba(111,143,167,0.2)',c:'#AFE1F6',l:'To Do'},inprogress:{bg:'rgba(185,156,109,0.2)',c:'#B99C6D',l:'In Progress'},done:{bg:'rgba(129,157,132,0.2)',c:'#819D84',l:'Done'},blocked:{bg:'rgba(200,80,80,0.15)',c:'#e87070',l:'Blocked'}};
                const s = sc[linkedTask.status] || sc.todo;
                return <span style={{ fontSize:9.5, fontWeight:700, color:s.c, background:s.bg, padding:'2px 6px', borderRadius:4, fontFamily:F }}>{s.l}</span>;
              })()}
              {linkedTask.due_date && <span style={{ fontSize:9.5, color:'rgba(175,225,246,0.4)', fontFamily:F }}>{linkedTask.due_date}</span>}
              {linkedTask.priority==="p1" && <span style={{ fontSize:9, fontWeight:700, color:'#e87070', fontFamily:F }}>P1</span>}
            </div>
            <button onClick={onOpenPlanner}
              style={{ fontSize:10, color:'rgba(175,225,246,0.5)', background:'none', border:'none', cursor:'pointer', fontFamily:F, padding:0, textDecoration:'underline' }}>
              View in Planner →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Toolbar ───────────────────────────────────────────────────────────────────
function Toolbar({ boardName, zoom, onZoomIn, onZoomOut, onZoomReset, onFitAll,
  nodeCount, cursors, onUndo, canUndo, onExport, exporting, onSearch, onSana, sanaOpen, onShortcuts, onOpenApp, onOpenPlanner }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const peerCount = Object.keys(cursors).length;
  const tbtn = (disabled) => ({
    display:'flex', alignItems:'center', gap:5, padding:'5px 9px',
    background:'transparent', border:`1px solid ${t.bd}`, borderRadius:7,
    fontSize:11, fontWeight:600, fontFamily:F,
    color: disabled ? '#c8d8e0' : '#6F8FA7',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
  });
  return (
    <div style={{ height:40, background:t.panel, borderBottom:`1px solid ${t.bd}`, display:'flex', alignItems:'center', gap:6, padding:'0 12px', flexShrink:0, fontFamily:F, overflow:'hidden' }}>
      <div style={{ display:'flex', alignItems:'center', gap:7, marginRight:4 }}>
        <span style={{ fontSize:13, fontWeight:700, color:t.tx }}>{boardName}</span>
        {nodeCount > 0 && <span style={{ fontSize:10, color:t.mut, fontWeight:500 }}>{nodeCount} {nodeCount===1?'item':'items'}</span>}
      </div>
      <div style={{ width:1, height:18, background:'#edf0f3' }}/>
      <div style={{ display:'flex', alignItems:'center', gap:2, background:'rgba(49,79,92,0.05)', borderRadius:8, padding:3 }}>
        <button onClick={onZoomOut} style={{ width:26, height:26, border:'none', background:'transparent', cursor:'pointer', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', color:t.accent }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
        <button onClick={onZoomReset} style={{ minWidth:42, padding:'0 4px', height:26, border:'none', background:'transparent', cursor:'pointer', borderRadius:6, fontSize:11, fontWeight:600, color:t.accent, fontFamily:F }}>
          {Math.round(zoom*100)}%
        </button>
        <button onClick={onZoomIn} style={{ width:26, height:26, border:'none', background:'transparent', cursor:'pointer', borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', color:t.accent }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </button>
      </div>
      <button onClick={onFitAll} disabled={nodeCount===0} title="Fit all (F)" style={tbtn(nodeCount===0)}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>
        Fit
      </button>
      <button onClick={onUndo} disabled={!canUndo} title="Undo (Cmd+Z)" style={tbtn(!canUndo)}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
        Undo
      </button>
      <div style={{ flex:1 }}/>
      {peerCount > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 9px', background:'rgba(45,160,106,0.07)', border:'1px solid rgba(45,160,106,0.2)', borderRadius:99 }}>
          <span style={{ width:7, height:7, borderRadius:'50%', background:'#2da06a', display:'inline-block', animation:'studioPulse 2s ease-in-out infinite' }}/>
          <span style={{ fontSize:11, color:'#1a7a3a', fontWeight:600 }}>{peerCount} live</span>
        </div>
      )}
      <div style={{ display:'flex', alignItems:'center', gap:3 }}>
        {/* Integration shortcuts */}
        <button onClick={onOpenApp} title="The App — view bugs & builds"
          style={{ height:28, padding:'0 9px', borderRadius:7, border:`1px solid ${t.bd}`, background:'transparent',
            cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontFamily:F, fontSize:11, fontWeight:600, color:t.mut, transition:'all .1s' }}
          onMouseEnter={e=>{e.currentTarget.style.background='#f4f7f9';e.currentTarget.style.borderColor='#c8d8e0';}}
          onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.borderColor='#dde8ed';}}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18"/></svg>
          The App
        </button>
        <button onClick={onOpenPlanner} title="Planner — push tasks"
          style={{ height:28, padding:'0 9px', borderRadius:7, border:`1px solid ${t.bd}`, background:'transparent',
            cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontFamily:F, fontSize:11, fontWeight:600, color:t.mut, transition:'all .1s' }}
          onMouseEnter={e=>{e.currentTarget.style.background='#f4f7f9';e.currentTarget.style.borderColor='#c8d8e0';}}
          onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.borderColor='#dde8ed';}}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          Planner
        </button>
        <div style={{ width:1, height:16, background:'#edf0f3', margin:'0 2px' }}/>
        {/* Search */}
        <button onClick={onSearch} title="Search (⌘F)"
          style={{ height:28, padding:'0 9px', borderRadius:7, border:`1px solid ${t.bd}`, background:'transparent',
            cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontFamily:F, fontSize:11, fontWeight:600, color:t.mut, transition:'all .1s' }}
          onMouseEnter={e=>{e.currentTarget.style.background='#f4f7f9';e.currentTarget.style.borderColor='#c8d8e0';}}
          onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.borderColor='#dde8ed';}}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          Search
        </button>
        {/* Sana */}
        <button onClick={onSana} title="Sana AI"
          style={{ height:28, padding:'0 9px', borderRadius:7, cursor:'pointer', fontFamily:F,
            border:'1px solid ' + (sanaOpen?'rgba(45,160,106,0.4)':'#dde8ed'),
            background:sanaOpen?'rgba(45,160,106,0.1)':'transparent',
            display:'flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600,
            color:sanaOpen?'#2da06a':'#6F8FA7', transition:'all .1s' }}
          onMouseEnter={e=>{if(!sanaOpen){e.currentTarget.style.background='rgba(45,160,106,0.06)';e.currentTarget.style.color='#2da06a';e.currentTarget.style.borderColor='rgba(45,160,106,0.25)';}}}
          onMouseLeave={e=>{if(!sanaOpen){e.currentTarget.style.background='transparent';e.currentTarget.style.color='#6F8FA7';e.currentTarget.style.borderColor='#dde8ed';}}}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"
              fill={sanaOpen?'#314F5C':'#9eb8c8'}/>
          </svg>
          Sana
        </button>
        {/* divider */}
        <div style={{ width:1, height:16, background:'#edf0f3', margin:'0 2px' }}/>
        {/* Export */}
        <button onClick={onExport} disabled={exporting}
          style={{ height:28, padding:'0 9px', borderRadius:7, border:`1px solid ${t.bd}`,
            background:'transparent', cursor:exporting?'default':'pointer', fontFamily:F,
            display:'flex', alignItems:'center', gap:5, fontSize:11, fontWeight:600,
            color:exporting?'#c8d8e0':'#6F8FA7', opacity:exporting?0.5:1, transition:'all .1s' }}
          onMouseEnter={e=>{if(!exporting){e.currentTarget.style.background='#f4f7f9';}}}
          onMouseLeave={e=>{e.currentTarget.style.background='transparent';}}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {exporting?'Exporting…':'Export'}
        </button>
        {/* Shortcuts */}
        <button onClick={onShortcuts} title="Shortcuts (?)"
          style={{ width:28, height:28, borderRadius:7, border:`1px solid ${t.bd}`, background:'transparent',
            cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:t.mut, transition:'all .1s' }}
          onMouseEnter={e=>{e.currentTarget.style.background='#f4f7f9';e.currentTarget.style.color='#6F8FA7';}}
          onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='#9eb8c8';}}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        </button>
      </div>
    </div>
  );
}

// ── Bottom palette ────────────────────────────────────────────────────────────
function BottomPalette({ mode, onSetMode, onAddNote, onAddHeading, onAddChecklist, onAddSection, onUploadImage, onAddLink, onShowTemplates }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const fileRef = useRef(null);

  const items = [
    { id:'note',      label:'Note',      bg:'#E0F5FC', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#314F5C" strokeWidth="2" strokeLinecap="round"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>, action: onAddNote },
    { id:'heading',   label:'Heading',   bg:'#f2e8d5', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9F7E4F" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><path d="M3 12h13M3 18h8"/></svg>, action: onAddHeading },
    { id:'checklist', label:'Checklist', bg:'#dceadd', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#204C2C" strokeWidth="2" strokeLinecap="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>, action: onAddChecklist },
    { id:'image',     label:'Image',     bg:'#fff',    border:`1px solid ${t.bd}`, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6F8FA7" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>, action: () => fileRef.current?.click() },
    { id:'link', label:'Link', bg:'#edf4f9', icon:<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6F8FA7" strokeWidth="2" strokeLinecap="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>, action:onAddLink },
    { id:'sep1' },
    { id:'section',   label:'Section',   bg:'#f4f6f8', dashed:true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(49,79,92,0.4)" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="3"/></svg>, action: onAddSection },
    { id:'sep2' },
    { id:'draw',      label:'Draw',      bg:'#f4f6f8', toggle:'draw', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={mode==='draw'?'#fff':'rgba(49,79,92,0.4)'} strokeWidth="2" strokeLinecap="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/></svg> },
    { id:'connect',   label:'Connect',   bg:'#f4f6f8', toggle:'connect', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={mode==='connect'?'#fff':'rgba(49,79,92,0.4)'} strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg> },
    { id:'sep3' },
    { id:'template',  label:'Templates', bg:'#f2e8d5', isTemplate:true, icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#B99C6D" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/></svg> },
  ];

  return (
    <div className="studio-palette" style={{ position:'absolute', bottom:14, left:'50%', transform:'translateX(-50%)',
      background:t.panel, borderRadius:13, boxShadow:'0 4px 24px rgba(15,30,40,0.15), 0 0 0 1px rgba(15,30,40,0.06)',
      display:'flex', alignItems:'center', gap:2, padding:'6px 10px', fontFamily:F, zIndex:100 }}>
      {items.map((item, i) => {
        if (item.id.startsWith('sep')) return <div key={i} style={{ width:1, height:28, background:'#edf0f3', margin:'0 3px' }}/>;
        const isActive = item.toggle && mode === item.toggle;
        return (
          <button key={item.id}
            onClick={() => { if (item.toggle) { onSetMode(isActive ? null : item.toggle); } else if (item.isTemplate) { onShowTemplates && onShowTemplates(); } else if (item.id==='note') { onAddNote(); } else if (item.id==='heading') { onAddHeading(); } else if (item.id==='checklist') { onAddChecklist(); } else if (item.id==='section') { onAddSection(); } else if (item.id==='link') { onAddLink(); } else if (item.id==='image') { fileRef.current?.click(); } }}
            title={item.label}
            style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:3, padding:'5px 8px', borderRadius:9,
              cursor:'pointer', border: isActive ? '1.5px solid #314F5C' : '1px solid transparent',
              background: isActive ? '#E0F5FC' : 'transparent', transition:'all .12s' }}>
            <div style={{ width:28, height:28, borderRadius:7,
              background: isActive ? '#314F5C' : (item.bg||'#f4f6f8'),
              border: item.dashed ? '2px dashed rgba(49,79,92,0.18)' : (item.border||'none'),
              display:'flex', alignItems:'center', justifyContent:'center' }}>
              {item.icon}
            </div>
            <span style={{ fontSize:9, color: isActive ? '#314F5C' : '#6F8FA7', fontWeight: isActive ? 700 : 600 }}>{item.label}</span>
          </button>
        );
      })}
      {/* FIXED: onChange now properly calls onUploadImage */}
      <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }}
        onChange={e => { if (e.target.files[0]) { onUploadImage(e.target.files[0]); e.target.value = ''; }}}/>
    </div>
  );
}

// ── Setup screen ──────────────────────────────────────────────────────────────
function SetupScreen({ onRetry }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', background:t.bg, padding:32 }}>
      <div style={{ maxWidth:540, width:'100%', background:t.panel, borderRadius:16, boxShadow:'0 4px 24px rgba(15,30,40,0.08)', overflow:'hidden' }}>
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${t.bd}`, display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:36, height:36, borderRadius:10, background:'rgba(49,79,92,0.08)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#314F5C" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M12 8v8M8 12h8"/></svg>
          </div>
          <div>
            <div style={{ fontSize:14, fontWeight:700, color:t.tx, fontFamily:F }}>One-time setup required</div>
            <div style={{ fontSize:11.5, color:t.mut, marginTop:2, fontFamily:F }}>Run this SQL in Supabase to enable Studio</div>
          </div>
        </div>
        <div style={{ padding:'16px 24px' }}>
          <div style={{ background:'#0f1e28', borderRadius:10, padding:'14px 16px', position:'relative', maxHeight:260, overflowY:'auto' }}>
            <pre style={{ margin:0, fontFamily:'monospace', fontSize:10.5, color:'#AFE1F6', lineHeight:1.7, whiteSpace:'pre-wrap' }}>{SETUP_SQL}</pre>
            <button onClick={() => { navigator.clipboard.writeText(SETUP_SQL); setCopied(true); setTimeout(()=>setCopied(false),2000); }}
              style={{ position:'sticky', top:0, float:'right', padding:'4px 10px', background: copied?'#2da06a':'rgba(175,225,246,0.12)', color: copied?'#fff':'#AFE1F6', border:'1px solid rgba(175,225,246,0.2)', borderRadius:7, fontSize:10.5, fontWeight:600, cursor:'pointer', fontFamily:F, transition:'all .15s' }}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:14 }}>
            <a href="https://supabase.com/dashboard/project/dekxnqbjzgkvltclthqw/sql/new" target="_blank" rel="noopener noreferrer"
              style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'9px 0', background:t.accent, color:'#E0F5FC', borderRadius:9, fontSize:12.5, fontWeight:600, textDecoration:'none', fontFamily:F }}>
              Open Supabase SQL Editor
            </a>
            <button onClick={onRetry} style={{ padding:'9px 18px', background:'transparent', border:`1px solid ${t.bd}`, borderRadius:9, fontSize:12.5, fontWeight:600, color:t.mut, cursor:'pointer', fontFamily:F }}>
              Done — Retry
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Studio ───────────────────────────────────────────────────────────────
function StudioBody({ session }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [boards,        setBoards]       = useState([]);
  const [activeBoardId, setActiveBoardId]= useState(null);
  const [nodes,         setNodes]        = useState([]);
  const [edges,         setEdges]        = useState([]);
  const [panX,          setPanX]         = useState(40);
  const [panY,          setPanY]         = useState(40);
  const [zoom,          setZoom]         = useState(1);
  const [selectedId,    setSelectedId]   = useState(null);
  const [editingId,     setEditingId]    = useState(null);
  const [editingCheckIdx, setEditingCheckIdx] = useState(null);
  const [draggingId,    setDraggingId]   = useState(null);
  const [isPanning,     setIsPanning]    = useState(false);
  const [cursors,       setCursors]      = useState({});
  const [loading,       setLoading]      = useState(true);
  const [needsSetup,    setNeedsSetup]   = useState(false);
  const [uploadingImage,setUploading]    = useState(false);
  const [exporting,     setExporting]    = useState(false);
  const [me,            setMe]           = useState(null);
  const [mode,          setMode]         = useState(null);
  const [connecting,    setConnecting]   = useState(null);
  const [drawPath,      setDrawPath]     = useState(null);
  const [mouseCanvasPos,setMouseCanvasPos] = useState(null);
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [selectedIds,   setSelectedIds]   = useState([]);
  const [contextMenu,   setContextMenu]   = useState(null);
  const [linkedTask,    setLinkedTask]    = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showSearch,    setShowSearch]    = useState(false);
  const [searchQuery,   setSearchQuery]   = useState('');
  const [searchIdx,     setSearchIdx]     = useState(0);
  const [showSana,      setShowSana]      = useState(false);
  const [showTemplate,  setShowTemplate]  = useState(false);
  // Undo stack — each entry is a { nodes, edges } snapshot
  const [undoStack, setUndoStack] = useState([]);
  // Track spawn offset for staggering new nodes
  const spawnOffsetRef = useRef(0);

  const canvasRef       = useRef(null);
  const dragRef         = useRef(null);
  const panRef          = useRef(null);
  const zoomRef         = useRef(1);
  const panXRef         = useRef(40);
  const panYRef         = useRef(40);
  const realtimeRef     = useRef(null);
  const edgesRTRef      = useRef(null);
  const cursorRef       = useRef(null);
  const cursorThrottle  = useRef(0);
  const drawingRef      = useRef(false);
  const drawPointsRef   = useRef([]);
  const nodesRef        = useRef([]);
  const edgesRef        = useRef([]);
  const activeBoardRef  = useRef(null);
  const meRef           = useRef(null);
  const selectedIdsRef  = useRef([]);
  const shiftKeyRef     = useRef(false);

  // Keep refs in sync
  useEffect(() => { zoomRef.current = zoom; }, [zoom]);
  useEffect(() => { panXRef.current = panX; }, [panX]);
  useEffect(() => { panYRef.current = panY; }, [panY]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);
  useEffect(() => { activeBoardRef.current = activeBoardId; }, [activeBoardId]);
  useEffect(() => { meRef.current = me; }, [me]);
  useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);

  useEffect(() => {
    if (!session?.user) return;
    const email = session.user.email || '';
    // Prefer team_members profile data (real initials, name, brand colour)
    supabase.from('team_members').select('name, initials, color').eq('email', email).maybeSingle()
      .then(({ data: tm }) => {
        if (tm) {
          setMe({ id: session.user.id, name: tm.name || email.split('@')[0], initials: tm.initials || '?', color: tm.color || '#314F5C' });
        } else {
          // Fallback: derive from email
          const parts = email.split('@')[0].split(/[._-]/);
          const name = parts.map(p => p.charAt(0).toUpperCase()+p.slice(1)).join(' ');
          const initials = parts.map(p => p[0]?.toUpperCase()).join('').slice(0,2) || '?';
          const colors = ['#314F5C','#819D84','#B99C6D','#6F8FA7','#204C2C'];
          setMe({ id: session.user.id, name, initials, color: colors[name.charCodeAt(0) % colors.length] });
        }
      });
  }, [session]); // eslint-disable-line

  useEffect(() => {
    if (!document.getElementById('studio-styles')) {
      const s = document.createElement('style');
      s.id = 'studio-styles';
      s.textContent = '@keyframes studioPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.6;transform:scale(1.15)}}';
      document.head.appendChild(s);
    }
    loadBoards();
    return () => {
      if (realtimeRef.current) supabase.removeChannel(realtimeRef.current);
      if (edgesRTRef.current) supabase.removeChannel(edgesRTRef.current);
      if (cursorRef.current) supabase.removeChannel(cursorRef.current);
    };
  }, []); // eslint-disable-line

  // ── Undo helpers ──────────────────────────────────────────────────────────────
  function pushUndo() {
    const snap = { nodes: nodesRef.current.map(n=>({...n})), edges: edgesRef.current.map(e=>({...e})) };
    setUndoStack(prev => [...prev.slice(-29), snap]);
  }
  function handleUndo() {
    setUndoStack(prev => {
      if (!prev.length) return prev;
      const snap = prev[prev.length-1];
      setNodes(snap.nodes);
      setEdges(snap.edges);
      setSelectedId(null); setEditingId(null);
      return prev.slice(0,-1);
    });
  }

  // ── Fit all ───────────────────────────────────────────────────────────────────
  function fitAll() {
    const cur = nodesRef.current;
    if (!cur.length || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const pad = 60;
    const minX = Math.min(...cur.map(n=>n.x));
    const minY = Math.min(...cur.map(n=>n.y));
    const maxX = Math.max(...cur.map(n=>n.x+n.w));
    const maxY = Math.max(...cur.map(n=>n.y+n.h));
    const bw=maxX-minX||1, bh=maxY-minY||1;
    const nz = Math.min((rect.width-pad*2)/bw, (rect.height-pad*2)/bh, 2);
    setZoom(nz);
    setPanX(rect.width/2  - (minX+bw/2)*nz);
    setPanY(rect.height/2 - (minY+bh/2)*nz);
  }

  // ── Duplicate node ────────────────────────────────────────────────────────────
  async function duplicateNode(node) {
    const cur = nodesRef.current;
    const maxZ = cur.reduce((m,n)=>Math.max(m,n.z_index||1),0);
    const { id:_id, created_at:_ca, updated_at:_ua, ...rest } = node;
    const row = { ...rest, x:node.x+24, y:node.y+24, z_index:maxZ+1, created_by:meRef.current?.initials||'?' };
    pushUndo();
    try {
      const { data, error } = await supabase.from('studio_nodes').insert(row).select().single();
      if (!error && data) setSelectedId(data.id);
    } catch (_e) {}
  }

  // ── Shift key tracking ─────────────────────────────────────────────────────
  useEffect(() => {
    const dn = e => { if(e.key==='Shift') shiftKeyRef.current=true; };
    const up = e => { if(e.key==='Shift') shiftKeyRef.current=false; };
    window.addEventListener('keydown', dn);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', dn); window.removeEventListener('keyup', up); };
  }, []);

  // ── Keyboard handler ──────────────────────────────────────────────────────────
  useEffect(() => {
    function onKey(e) {
      const meta = e.metaKey || e.ctrlKey;
      if (e.key === 'Escape') {
        setContextMenu(null);
        if (mode) { setMode(null); setConnecting(null); setDrawPath(null); drawingRef.current=false; return; }
        if (editingId) { setEditingId(null); setEditingCheckIdx(null); return; }
        setSelectedId(null); setSelectedIds([]); return;
      }
      if (e.key==='f' && !meta && !editingId) { fitAll(); return; }
      if (e.key==='?' && !editingId) { setShowShortcuts(s=>!s); return; }
      if (e.key==='f' && meta) { e.preventDefault(); setShowSearch(s=>!s); if(showSearch){setSearchQuery('');setSearchIdx(0);} return; }
      if (e.key==='z' && meta) { e.preventDefault(); handleUndo(); return; }
      if (e.key==='d' && meta && selectedId && !editingId) {
        e.preventDefault();
        const node = nodesRef.current.find(n=>n.id===selectedId);
        if (node) duplicateNode(node);
        return;
      }
      // Delete: remove ALL selected nodes
      if ((e.key==='Delete'||e.key==='Backspace') && !editingId) {
        const idsToDelete = selectedIdsRef.current.length > 0
          ? [...new Set([selectedId, ...selectedIdsRef.current].filter(Boolean))]
          : selectedId ? [selectedId] : [];
        if (idsToDelete.length > 0) {
          pushUndo();
          idsToDelete.forEach(id => deleteNode(id));
          setSelectedId(null); setSelectedIds([]);
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, editingId, selectedId, undoStack, showSearch]); // eslint-disable-line

  async function loadBoards() {
    setLoading(true);
    try {
    const { data, error } = await supabase.from('studio_boards').select('*').order('created_at', { ascending:true });
    if (error) {
      if (error.code === 'PGRST205' || (error.message||'').includes('studio_')) setNeedsSetup(true);
      setLoading(false); return;
    }
    let bds = data || [];
    // If no boards exist yet, create a first 'Main board' with a real UUID
    if (bds.length === 0) {
      const { data:nb } = await supabase.from('studio_boards').insert({ name:'Main board', created_by:'system' }).select().single();
      if (nb) bds = [nb];
    }
    setBoards(bds);
    // Check if opened from Planner with a pre-selected board
    const targetBoard = sessionStorage.getItem('studio_open_board');
    if (targetBoard) {
      sessionStorage.removeItem('studio_open_board');
      if (bds.find(b => b.id === targetBoard)) {
        setActiveBoardId(targetBoard);
        activeBoardRef.current = targetBoard;
        await loadBoardData(targetBoard);
        setupCursorChannel();
        setLoading(false);
        setSidebarOpen(true); // expand sidebar so user sees the Planner panel
        return;
      }
    }
    // Default to first available board
    const firstId = bds[0]?.id;
    if (!firstId) { setLoading(false); return; }
    if (!activeBoardRef.current) {
      setActiveBoardId(firstId);
      activeBoardRef.current = firstId;
    }
    await loadBoardData(activeBoardRef.current);
    setupCursorChannel();
    setLoading(false);
    } catch (_e) { setLoading(false); }
  }

  async function loadBoardData(boardId) {
    const [{ data:nodeData, error:nErr }, { data:edgeData }, { data:taskData }] = await Promise.all([
      supabase.from('studio_nodes').select('*').eq('board_id', boardId).order('z_index', { ascending:true }),
      supabase.from('studio_edges').select('*').eq('board_id', boardId),
      supabase.from('admin_tasks').select('id,title,status,priority,due_date,studio_board_id').eq('studio_board_id', boardId).limit(1).maybeSingle(),
    ]);
    if (nErr) { if (nErr.code === 'PGRST205') setNeedsSetup(true); return; }
    setNodes(nodeData || []);
    setEdges(edgeData || []);
    setLinkedTask(taskData || null);
    setUndoStack([]);
    setupRealtimeForBoard(boardId);
  }

  function setupRealtimeForBoard(boardId) {
    if (realtimeRef.current) supabase.removeChannel(realtimeRef.current);
    if (edgesRTRef.current) supabase.removeChannel(edgesRTRef.current);
    const ch = supabase.channel('studio-nodes-'+boardId)
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'studio_nodes', filter:'board_id=eq.'+boardId },
        ({ new:n }) => setNodes(prev => prev.some(x=>x.id===n.id) ? prev : [...prev,n]))
      .on('postgres_changes', { event:'UPDATE', schema:'public', table:'studio_nodes', filter:'board_id=eq.'+boardId },
        ({ new:n }) => setNodes(prev => prev.map(x => x.id===n.id ? n : x)))
      .on('postgres_changes', { event:'DELETE', schema:'public', table:'studio_nodes' },
        ({ old:o }) => setNodes(prev => prev.filter(x => x.id!==o.id)))
      .subscribe();
    realtimeRef.current = ch;
    const ec = supabase.channel('studio-edges-'+boardId)
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'studio_edges', filter:'board_id=eq.'+boardId },
        ({ new:e }) => setEdges(prev => prev.some(x=>x.id===e.id) ? prev : [...prev,e]))
      .on('postgres_changes', { event:'DELETE', schema:'public', table:'studio_edges' },
        ({ old:e }) => setEdges(prev => prev.filter(x => x.id!==e.id)))
      .subscribe();
    edgesRTRef.current = ec;
  }

  function setupCursorChannel() {
    const cc = supabase.channel('studio-cursors')
      .on('broadcast', { event:'cursor' }, ({ payload:p }) => {
        if (!p || p.id === session?.user?.id) return;
        setCursors(prev => ({ ...prev, [p.id]: p }));
        setTimeout(() => setCursors(prev => { const n={...prev}; if(n[p.id]?.ts===p.ts) delete n[p.id]; return n; }), 4000);
      }).subscribe();
    cursorRef.current = cc;
  }

  async function switchBoard(boardId, isNew) {
    setActiveBoardId(boardId);
    setSelectedId(null); setEditingId(null); setEditingCheckIdx(null);
    setSelectedIds([]); setContextMenu(null); setShowSearch(false); setShowSana(false);
    setPanX(40); setPanY(40); setZoom(1);
    setMode(null); setConnecting(null);
    spawnOffsetRef.current = 0;
    await loadBoardData(boardId);
    if (isNew) setShowTemplate(true);
  }

  async function createBoard() {
    const color = BOARD_COLORS[boards.length % BOARD_COLORS.length];
    try {
      const { data, error } = await supabase.from('studio_boards').insert({ name:'Untitled board', color, created_by: meRef.current?.initials||'?' }).select().single();
      if (!error && data) { setBoards(prev=>[...prev,data]); switchBoard(data.id, true); }
    } catch (_e) {}
  }

  async function renameBoard(id, name) {
    try { await supabase.from('studio_boards').update({ name }).eq('id', id); } catch (_e) {}
    setBoards(prev => prev.map(b => b.id===id ? {...b,name} : b));
  }

  async function deleteBoard(id) {
    try { await supabase.from('studio_boards').delete().eq('id', id); } catch (_e) {}
    const remaining = boards.filter(b => b.id !== id);
    setBoards(remaining);
    if (remaining.length > 0) switchBoard(remaining[0].id);
  }

  // ── FIXED: spawn nodes at canvas center, staggered ──────────────────────────
  async function addNode(nodeType, extraPos, extraFields) {
    const dims = { note:{w:200,h:160}, heading:{w:240,h:110}, checklist:{w:220,h:180}, section:{w:300,h:220}, link:{w:220,h:100} };
    const dim = dims[nodeType] || { w:200, h:160 };

    let spawnX, spawnY;
    if (extraPos) {
      spawnX = extraPos.x; spawnY = extraPos.y;
    } else {
      // Use canvas element rect — accounts for sidebar width correctly
      const rect = canvasRef.current?.getBoundingClientRect();
      const cw = rect ? rect.width  : 600;
      const ch = rect ? rect.height : 400;
      const offset = spawnOffsetRef.current * 24; // cascade 24px each
      spawnOffsetRef.current = (spawnOffsetRef.current + 1) % 8; // reset after 8
      spawnX = (cw/2 - panXRef.current) / zoomRef.current - dim.w/2 + offset;
      spawnY = (ch/2 - panYRef.current) / zoomRef.current - dim.h/2 + offset;
    }

    const cur = nodesRef.current;
    const maxZ = cur.reduce((m,n) => Math.max(m, n.z_index||1), 0);
    const row = {
      board_id: activeBoardRef.current, node_type: nodeType, type: nodeType,
      x: spawnX, y: spawnY, ...dim,
      content: '', subtitle: '',
      checked_items: nodeType==='checklist' ? [{text:'',done:false}] : [],
      color: DEFAULT_COLOR.bg, z_index: maxZ+1,
      created_by: meRef.current?.initials||'?',
      ...(extraFields||{}),
    };
    pushUndo();
    try {
      const { data, error } = await supabase.from('studio_nodes').insert(row).select().single();
      if (!error && data) {
        setSelectedId(data.id);
        setEditingId(data.id);
        if (nodeType === 'checklist') setEditingCheckIdx(0);
      }
    } catch (_e) {}
  }

  // ── FIXED: uploadImage now receives file directly ────────────────────────────
  async function uploadImage(file) {
    if (!file) return;
    setUploading(true);
    try {
      const ext = (file.name.split('.').pop()||'jpg').toLowerCase();
      const path = `node-${Date.now()}.${ext}`;
      const { error:upErr } = await supabase.storage.from('studio-images').upload(path, file, { contentType:file.type });
      if (upErr) {
        await fetch('/api/studio-setup', { method:'POST' }).catch(()=>{});
        const { error:retryErr } = await supabase.storage.from('studio-images').upload(path, file, { contentType:file.type });
        if (retryErr) throw retryErr;
      }
      const { data:{ publicUrl } } = supabase.storage.from('studio-images').getPublicUrl(path);
      const rect = canvasRef.current?.getBoundingClientRect();
      const cw = rect ? rect.width : 600;
      const ch = rect ? rect.height : 400;
      const cur = nodesRef.current;
      const maxZ = cur.reduce((m,n) => Math.max(m, n.z_index||1), 0);
      await supabase.from('studio_nodes').insert({
        board_id: activeBoardRef.current, node_type:'image', type:'image',
        x: (cw/2-panXRef.current)/zoomRef.current-160,
        y: (ch/2-panYRef.current)/zoomRef.current-120,
        w:320, h:240, content:'', image_url:publicUrl,
        color:'#ffffff', z_index:maxZ+1, created_by: meRef.current?.initials||'?',
      });
    } catch (_e) {
      alert('Image upload failed. Make sure the studio-images bucket exists in Supabase Storage.');
    }
    setUploading(false);
  }

  // ── FIXED: updateNodeField never passes undefined values ────────────────────
  async function updateNodeField(id, fields) {
    const clean = Object.fromEntries(Object.entries(fields).filter(([,v]) => v !== undefined));
    if (Object.keys(clean).length === 0) return;
    setNodes(prev => prev.map(n => n.id===id ? {...n,...clean} : n));
    try { await supabase.from('studio_nodes').update({ ...clean, updated_at: new Date().toISOString() }).eq('id', id); } catch (_e) {}
  }

  async function deleteNode(id) {
    setNodes(prev => prev.filter(n => n.id!==id));
    setEdges(prev => prev.filter(e => e.from_id!==id && e.to_id!==id));
    if (selectedId===id) setSelectedId(null);
    if (editingId===id) setEditingId(null);
    setSelectedIds(prev => prev.filter(x => x!==id));
    try {
      await supabase.from('studio_nodes').delete().eq('id', id);
      await supabase.from('studio_edges').delete().or(`from_id.eq.${id},to_id.eq.${id}`);
    } catch (_e) {}
  }

  async function handleConnectClick(nodeId) {
    if (!connecting?.fromId) {
      setConnecting({ fromId: nodeId });
    } else if (connecting.fromId === nodeId) {
      setConnecting(null);
      setMode(null); // cancel if clicking same node
    } else {
      try {
        const { data } = await supabase.from('studio_edges').insert({ board_id:activeBoardRef.current, from_id:connecting.fromId, to_id:nodeId, color:'#314F5C' }).select().single();
        if (data) setEdges(prev => [...prev, data]);
      } catch (_e) {}
      setConnecting(null);
      setMode(null); // exit connect mode after creating connection
    }
  }

  // ── Link node ─────────────────────────────────────────────────────────────────
  async function addLinkNode() {
    const url = window.prompt('Paste a URL:');
    if (!url?.trim()) return;
    const cleanUrl = url.trim().startsWith('http') ? url.trim() : 'https://' + url.trim();
    let title = cleanUrl.replace(/^https?:\/\//,'').split('/')[0];
    try {
      const res = await fetch('https://api.allorigins.win/get?url=' + encodeURIComponent(cleanUrl));
      const d = await res.json();
      const m = d.contents?.match(/<title[^>]*>([^<]+)<\/title>/i);
      if (m) title = m[1].trim().slice(0,60);
    } catch (_e) {}
    const favicon = 'https://www.google.com/s2/favicons?domain=' + encodeURIComponent(cleanUrl) + '&sz=32';
    await addNode('link', undefined, { content: JSON.stringify({url:cleanUrl,title,favicon}) });
  }

  // ── Node locking ───────────────────────────────────────────────────────────────
  async function toggleNodeLock(id) {
    const node = nodesRef.current.find(n=>n.id===id);
    if (!node) return;
    const newLocked = !node.locked;
    setNodes(prev => prev.map(n => n.id===id ? {...n, locked:newLocked} : n));
    Promise.resolve(supabase.from('studio_nodes').update({locked:newLocked}).eq('id',id)).catch(()=>{});
  }

  // ── Templates ─────────────────────────────────────────────────────────────────
  async function applyTemplate(template) {
    setShowTemplate(false);
    const maxZ = nodesRef.current.reduce((m,n)=>Math.max(m,n.z_index||1),0);
    const rows = template.nodes.map((n,i) => ({
      board_id:activeBoardRef.current, node_type:n.node_type, type:n.type,
      x:n.x, y:n.y, w:n.w, h:n.h, content:n.content||'', subtitle:n.subtitle||'',
      color:n.color||DEFAULT_COLOR.bg, checked_items:n.checked_items||[], z_index:maxZ+i+1,
      created_by:meRef.current?.initials||'?',
    }));
    try {
      const { data } = await supabase.from('studio_nodes').insert(rows).select();
      if (data) setNodes(prev=>[...prev,...data]);
      await supabase.from('studio_boards').update({name:template.name,color:template.color}).eq('id',activeBoardRef.current);
      setBoards(prev=>prev.map(b=>b.id===activeBoardRef.current?{...b,name:template.name,color:template.color}:b));
    } catch (_e) {}
    setTimeout(()=>fitAll(),200);
  }

  // ── Search ─────────────────────────────────────────────────────────────────────
  function getSearchResults() {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return nodesRef.current.filter(n => {
      const text = [n.content, n.subtitle, ...(Array.isArray(n.checked_items)?n.checked_items.map(i=>i.text):[])].join(' ').toLowerCase();
      return text.includes(q);
    });
  }
  function searchNext(dir) {
    const results = getSearchResults();
    if (!results.length) return;
    const idx = ((searchIdx+dir)+results.length) % results.length;
    setSearchIdx(idx);
    const node = results[idx];
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPanX(rect.width/2-(node.x+node.w/2)*zoomRef.current);
    setPanY(rect.height/2-(node.y+node.h/2)*zoomRef.current);
    setSelectedId(node.id);
  }

  // ── Sana drop to canvas ────────────────────────────────────────────────────────
  async function dropSanaToCanvas(text) {
    if (!text || !activeBoardRef.current) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    const cw = rect?.width||600, ch = rect?.height||400;
    const cx = (cw/2 - panXRef.current) / zoomRef.current;
    const cy = (ch/2 - panYRef.current) / zoomRef.current;

    // Split numbered/bulleted lists into multiple notes
    const numbered = text.match(/^\d+\.\s+.+$/gm);
    const bulleted  = text.match(/^[-\u2022*]\s+.+$/gm);
    const items = numbered || bulleted;
    if (items && items.length >= 2 && items.length <= 10) {
      const cols = items.length <= 4 ? items.length : Math.ceil(Math.sqrt(items.length));
      const nW=200, nH=120, gap=18;
      const startX = cx - (cols*(nW+gap)-gap)/2;
      const startY = cy - 80;
      const colors = ['#E0F5FC','#dceadd','#f2e8d5','#d4e8f0','#E0F5FC','#dceadd'];
      for (let i=0; i<items.length; i++) {
        const itemText = items[i].replace(/^(\d+\.\s*|[-\u2022*]\s*)/, '').trim();
        await addNode('note', { x: startX+(i%cols)*(nW+gap), y: startY+Math.floor(i/cols)*(nH+gap+8) }, { content:itemText, color:colors[i%colors.length] });
      }
    } else {
      await addNode('note', { x:cx-110, y:cy-80 }, { content:text.slice(0,500), color:'#E0F5FC' });
    }
  }

  // ── Planner integration ──────────────────────────────────────────────────────
  async function pushChecklistToPlanner(node) {
    const items = Array.isArray(node.checked_items) ? node.checked_items : [];
    const pending = items.filter(it => it.text && it.text.trim() && !it.done);
    if (!pending.length) return;
    const boardName = boards.find(b=>b.id===activeBoardRef.current)?.name || 'Studio';
    const rows = pending.map(it => ({
      title: it.text.trim(),
      status: 'todo',
      priority: 'p3',
      tags: ['Studio'],
      studio_board_id: activeBoardRef.current,
      description: 'From Studio board: ' + boardName,
    }));
    try {
      await supabase.from('admin_tasks').insert(rows);
      // Mark pushed items as done in Studio
      const newItems = items.map(it => (!it.done && it.text?.trim()) ? {...it, done:true} : it);
      updateNodeField(node.id, { checked_items: newItems });
    } catch (_e) {}
  }

  function openPlanner() {
    const navLinks = [...document.querySelectorAll('a, button, [role="button"]')];
    const plannerLink = navLinks.find(el => el.textContent.trim() === 'Planner');
    if (plannerLink) plannerLink.click();
    else window.location.href = '/planner';
  }

  function bringToFront(id) {
    const cur = nodesRef.current;
    const maxZ = cur.reduce((m,n) => Math.max(m, n.z_index||1), 0);
    setNodes(prev => prev.map(n => n.id===id ? {...n,z_index:maxZ+1} : n));
    Promise.resolve(supabase.from('studio_nodes').update({z_index:maxZ+1}).eq('id',id)).catch(()=>{});
  }
  function sendToBack(id) {
    const cur = nodesRef.current;
    const minZ = cur.reduce((m,n) => Math.min(m, n.z_index||1), 999);
    setNodes(prev => prev.map(n => n.id===id ? {...n,z_index:Math.max(0,minZ-1)} : n));
    Promise.resolve(supabase.from('studio_nodes').update({z_index:Math.max(0,minZ-1)}).eq('id',id)).catch(()=>{});
  }

  function startNodeDrag(e, node) {
    e.stopPropagation();
    if (mode==='connect') return;
    // Commit any active edit
    if (editingId===node.id) {
      const a = document.activeElement;
      if (a && (a.tagName==='TEXTAREA'||a.tagName==='INPUT')) a.blur();
      setEditingId(null); setEditingCheckIdx(null);
    }

    const { clientX, clientY } = getEventPos(e);

    // Multi-drag: if this node is part of a multi-selection, drag all of them
    const allSelected = selectedIdsRef.current.length > 0
      ? [...new Set([selectedId, ...selectedIdsRef.current].filter(Boolean))]
      : [node.id];
    const isMulti = allSelected.length > 1 && allSelected.includes(node.id);
    const nodesToDrag = isMulti ? allSelected : [node.id];
    const origPos = {};
    nodesRef.current.forEach(n => { if (nodesToDrag.includes(n.id)) origPos[n.id] = {x:n.x, y:n.y}; });

    dragRef.current = { ids:nodesToDrag, sx:clientX, sy:clientY, origPos, hasMoved:false };
    setDraggingId(node.id);

    // Bring dragged nodes to front
    const maxZ = nodesRef.current.reduce((m,n) => Math.max(m, n.z_index||1), 0);
    setNodes(prev => prev.map((n,i) => nodesToDrag.includes(n.id) ? {...n, z_index:maxZ+i+1} : n));

    function onMove(ev) {
      if (!dragRef.current) return;
      const { clientX:tx, clientY:ty } = getEventPos(ev);
      const dx=(tx-dragRef.current.sx)/zoomRef.current;
      const dy=(ty-dragRef.current.sy)/zoomRef.current;
      if (!dragRef.current.hasMoved && (Math.abs(dx)>2||Math.abs(dy)>2)) dragRef.current.hasMoved=true;
      const op = dragRef.current.origPos;
      const snap = shiftKeyRef.current;
      setNodes(prev => prev.map(n => {
        if (!op[n.id]) return n;
        const nx=op[n.id].x+dx, ny=op[n.id].y+dy;
        return {...n, x:snap?Math.round(nx/SNAP_GRID)*SNAP_GRID:nx, y:snap?Math.round(ny/SNAP_GRID)*SNAP_GRID:ny};
      }));
    }
    function onUp() {
      if (dragRef.current) {
        const { ids, hasMoved } = dragRef.current;
        if (hasMoved) {
          const cur = nodesRef.current;
          ids.forEach(_id => {
            const _n = cur.find(x=>x.id===_id);
            if (_n) Promise.resolve(supabase.from('studio_nodes').update({x:_n.x,y:_n.y}).eq('id',_id)).catch(()=>{});
          });
        }
        dragRef.current = null; setDraggingId(null);
      }
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    }
    // Use pointer events — works for both mouse and touch reliably
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  function startResize(e, node) {
    e.stopPropagation();
    const { clientX:sx, clientY:sy } = getEventPos(e);
    const ow=node.w, oh=node.h;
    function onMove(ev) {
      const { clientX:ex, clientY:ey } = getEventPos(ev);
      const dw=(ex-sx)/zoomRef.current, dh=(ey-sy)/zoomRef.current;
      setNodes(prev => prev.map(n => n.id===node.id ? {...n, w:Math.max(120,ow+dw), h:Math.max(80,oh+dh)} : n));
    }
    function onUp() {
      const n = nodesRef.current.find(x=>x.id===node.id);
      if (n) Promise.resolve(supabase.from('studio_nodes').update({w:n.w,h:n.h}).eq('id',node.id)).catch(()=>{});
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    }
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  // ── Canvas events ─────────────────────────────────────────────────────────────
  const onCanvasMouseDown = useCallback((e) => {
    const isBg = e.target===canvasRef.current || e.target.dataset.bg==='1';
    if (!isBg) return;

    if (mode==='draw') {
      const { clientX:_dx, clientY:_dy } = getEventPos(e);
      const rect = canvasRef.current.getBoundingClientRect();
      const cx=(_dx-rect.left-panXRef.current)/zoomRef.current;
      const cy=(_dy-rect.top-panYRef.current)/zoomRef.current;
      drawingRef.current = true;
      drawPointsRef.current = [{x:cx,y:cy}];
      setDrawPath(`M ${cx} ${cy}`);
      return;
    }
    // FIXED: clicking canvas in connect mode exits both connecting AND connect mode
    if (mode==='connect') {
      setConnecting(null);
      setMode(null);
      return;
    }
    const { clientX:_px, clientY:_py } = getEventPos(e);
    panRef.current = { sx:_px, sy:_py, ox:panXRef.current, oy:panYRef.current };
    setContextMenu(null);
    setIsPanning(true); setSelectedId(null); setSelectedIds([]); setEditingId(null); setEditingCheckIdx(null);
  }, [mode]);

  const onCanvasMouseMove = useCallback((e) => {
    const { clientX, clientY } = getEventPos(e);
    // Node drag is handled exclusively by window-level listeners in startNodeDrag.
    // Skipping here prevents double-update and canvas-leave drag breakage.
    if (dragRef.current) return;
    if (panRef.current) {
      setPanX(panRef.current.ox+(clientX-panRef.current.sx));
      setPanY(panRef.current.oy+(clientY-panRef.current.sy));
      return;
    }
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const cx=(clientX-rect.left-panXRef.current)/zoomRef.current;
      const cy=(clientY-rect.top-panYRef.current)/zoomRef.current;
      setMouseCanvasPos({ x:cx, y:cy });
      if (mode==='draw' && drawingRef.current) {
        drawPointsRef.current.push({x:cx,y:cy});
        const pts = drawPointsRef.current;
        setDrawPath(pts.reduce((d,p,i) => d+(i===0?`M ${p.x} ${p.y}`:`L ${p.x} ${p.y}`),''));
      }
    }
    const now = Date.now();
    if (now-cursorThrottle.current < 60 || !cursorRef.current || !meRef.current || !canvasRef.current) return;
    cursorThrottle.current = now;
    const rect2 = canvasRef.current.getBoundingClientRect();
    const cx=(clientX-rect2.left-panXRef.current)/zoomRef.current;
    const cy=(clientY-rect2.top-panYRef.current)/zoomRef.current;
    cursorRef.current.send({ type:'broadcast', event:'cursor',
      payload: { id:session?.user?.id, name:meRef.current.name, initials:meRef.current.initials, color:meRef.current.color, x:cx, y:cy, ts:now }
    }).catch(()=>{});
  }, [mode, session]);

  const onCanvasMouseUp = useCallback(async (_e) => {
    if (dragRef.current) {
      const { id, hasMoved } = dragRef.current;
      if (hasMoved) {
        const n = nodesRef.current.find(x=>x.id===id);
        if (n) Promise.resolve(supabase.from('studio_nodes').update({x:n.x,y:n.y}).eq('id',id)).catch(()=>{});
      }
      dragRef.current = null; setDraggingId(null);
    }
    if (panRef.current) { panRef.current=null; setIsPanning(false); }

    if (mode==='draw' && drawingRef.current) {
      drawingRef.current = false;
      const pts = drawPointsRef.current;
      if (pts.length > 2) {
        const xs=pts.map(p=>p.x), ys=pts.map(p=>p.y);
        const minX=Math.min(...xs), minY=Math.min(...ys);
        const maxX=Math.max(...xs), maxY=Math.max(...ys);
        const w=Math.max(maxX-minX, 10), h=Math.max(maxY-minY, 10);
        const cur = nodesRef.current;
        const maxZ = cur.reduce((m,n)=>Math.max(m,n.z_index||1),0);
        const pathStr = pts.reduce((d,p,i) => d+(i===0?`M ${p.x} ${p.y}`:`L ${p.x} ${p.y}`),'');
        try {
          const { data } = await supabase.from('studio_nodes').insert({
            board_id: activeBoardRef.current, node_type:'drawing', type:'drawing',
            x:minX-5, y:minY-5, w:w+10, h:h+10,
            content: pathStr, color:t.accent, z_index:maxZ+1,
            created_by: meRef.current?.initials||'?',
          }).select().single();
          if (data) setNodes(prev => [...prev, data]);
        } catch (_e) {}
      }
      setDrawPath(null);
      drawPointsRef.current = [];
    }
  }, [mode]);

  const onDblClick = useCallback((e) => {
    if (mode) return;
    const isBg = e.target===canvasRef.current || e.target.dataset.bg==='1';
    if (!isBg) return;
    const rect = canvasRef.current.getBoundingClientRect();
    addNode('note', {
      x: (e.clientX-rect.left-panXRef.current)/zoomRef.current - 100,
      y: (e.clientY-rect.top-panYRef.current)/zoomRef.current - 80,
    });
  }, [mode]); // eslint-disable-line

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    setZoom(z => Math.min(3, Math.max(0.15, z*factor)));
  }, []);

  async function exportPNG() {
    if (!nodes.length) return;
    setExporting(true);
    try {
      if (!window.html2canvas) {
        await new Promise((res,rej) => {
          const s = document.createElement('script');
          s.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
          s.onload=res; s.onerror=rej;
          document.head.appendChild(s);
        });
      }
      // Fit all first so export captures everything
      fitAll();
      await new Promise(r => setTimeout(r, 120)); // let layout settle
      const boardName = boards.find(b=>b.id===activeBoardId)?.name || 'studio';
      const canvas = await window.html2canvas(canvasRef.current, {
        backgroundColor:'#f2f1ef', scale:2, useCORS:true, logging:false,
        ignoreElements: el => el.classList?.contains('studio-palette') || el.classList?.contains('studio-toolbar'),
      });
      const link = document.createElement('a');
      link.download = boardName.toLowerCase().replace(/\s+/g,'-') + '-' + new Date().toISOString().slice(0,10) + '.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (_e) { alert('Export failed — try zooming out first and then export.'); }
    setExporting(false);
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', background:t.bg }}>
      <div style={{ width:32, height:32, borderRadius:'50%', border:'2.5px solid rgba(49,79,92,0.12)', borderTop:'2.5px solid #314F5C', animation:'sanoireSpin .9s linear infinite' }}/>
    </div>
  );
  if (needsSetup) return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', background:t.bg }}>
      <SetupScreen onRetry={loadBoards} />
    </div>
  );

  const sortedNodes = [...nodes].sort((a,b) => (a.z_index||1)-(b.z_index||1));
  const activeBoard  = boards.find(b => b.id===activeBoardId);
  const isEmpty = nodes.length === 0;
  const cursorStyle = mode==='draw'||mode==='connect' ? 'crosshair' : isPanning||draggingId ? 'grabbing' : 'default';

  return (
    <>
    <div style={{ flex:1, display:'flex', height:'calc(100vh - 180px)', minHeight:480, overflow:'hidden', fontFamily:F, position:'relative' }}>
      <BoardsSidebar boards={boards} activeBoardId={activeBoardId}
        onSelect={switchBoard} onCreate={createBoard}
        onRename={renameBoard} onDelete={deleteBoard}
        open={sidebarOpen} onToggle={() => setSidebarOpen(o=>!o)}
        linkedTask={linkedTask} onOpenPlanner={openPlanner}/>

      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0, position:'relative' }}>
        <Toolbar boardName={activeBoard?.name||'Board'} zoom={zoom}
          onZoomIn={() => setZoom(z=>Math.min(3,z*1.15))}
          onZoomOut={() => setZoom(z=>Math.max(0.15,z/1.15))}
          onZoomReset={() => { setZoom(1); setPanX(40); setPanY(40); }}
          onFitAll={fitAll}
          nodeCount={nodes.length} cursors={cursors}
          onUndo={handleUndo} canUndo={undoStack.length>0}
          onExport={exportPNG} exporting={exporting}
          onSearch={() => { setShowSearch(s=>!s); setSearchQuery(''); setSearchIdx(0); }}
          onSana={() => setShowSana(s=>!s)} sanaOpen={showSana}
          onShortcuts={() => setShowShortcuts(true)}
          onOpenApp={() => window.sanoireNav && window.sanoireNav('appstudio')}
          onOpenPlanner={() => window.sanoireNav && window.sanoireNav('planner')} />

        <div ref={canvasRef} data-bg="1"
          style={{ flex:1, overflow:'hidden', position:'relative', cursor:cursorStyle,
            background:'#f0eeeb',
            backgroundImage:'radial-gradient(circle, rgba(49,79,92,0.1) 1px, transparent 1px)',
            backgroundSize:'28px 28px',
            backgroundPosition:`${panX%28}px ${panY%28}px` }}
          onMouseDown={onCanvasMouseDown}
          onMouseMove={onCanvasMouseMove}
          onMouseUp={onCanvasMouseUp}
          onMouseLeave={() => { if (panRef.current) { panRef.current=null; setIsPanning(false); } }}
          onTouchStart={onCanvasMouseDown}
          onTouchMove={onCanvasMouseMove}
          onTouchEnd={onCanvasMouseUp}
          onTouchCancel={onCanvasMouseUp}
          onDoubleClick={onDblClick}
          onWheel={onWheel}>

          <div data-bg="1" style={{ position:'absolute', top:0, left:0, width:0, height:0,
            transform:`translate(${panX}px,${panY}px) scale(${zoom})`, transformOrigin:'0 0' }}>

            <ConnectorLayer edges={edges} nodes={nodes} connecting={connecting} mousePos={mouseCanvasPos}/>

            {drawPath && (
              <svg style={{ position:'absolute', top:0, left:0, overflow:'visible', pointerEvents:'none' }}>
                <path d={drawPath} stroke="#314F5C" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.55"/>
              </svg>
            )}

            {/* Search highlight */}
            {showSearch && searchQuery && getSearchResults().map((n,i) => (
              <div key={'sh'+n.id} style={{ position:'absolute', left:n.x-4, top:n.y-4, width:n.w+8, height:n.h+8,
                borderRadius:15, border:`2.5px solid ${i===searchIdx?'#314F5C':'rgba(49,79,92,0.3)'}`,
                pointerEvents:'none', boxShadow:i===searchIdx?'0 0 0 3px rgba(49,79,92,0.15)':'none' }}/>
            ))}

            {sortedNodes.map(node => (
              <StudioNode key={node.id} node={node}
                selected={selectedId===node.id || selectedIds.includes(node.id)}
                editing={editingId===node.id}
                editingCheckIdx={editingId===node.id ? editingCheckIdx : null}
                connectMode={mode==='connect'}
                onDragStart={e => startNodeDrag(e, node)}
                onSelect={() => {
                  if (shiftKeyRef.current) {
                    setSelectedIds(prev => prev.includes(node.id) ? prev.filter(x=>x!==node.id) : [...prev, node.id]);
                  } else {
                    setSelectedId(node.id); setSelectedIds([]);
                  }
                }}
                onEdit={() => { setSelectedId(node.id); setSelectedIds([]); setEditingId(node.id); }}
                onEditCheckIdx={idx => { setSelectedId(node.id); setSelectedIds([]); setEditingId(node.id); setEditingCheckIdx(idx); }}
                onBlur={() => { setEditingId(null); setEditingCheckIdx(null); }}
                onContentChange={c => {
                  if (typeof c === 'string') updateNodeField(node.id, { content:c });
                  else if (Array.isArray(c)) updateNodeField(node.id, { checked_items:c });
                }}
                onSubtitleChange={s => updateNodeField(node.id, { subtitle:s })}
                onColorChange={c => updateNodeField(node.id, { color:c })}
                onDelete={() => { pushUndo(); deleteNode(node.id); }}
                onResizeStart={startResize}
                onConnectClick={() => handleConnectClick(node.id)}
                onContextMenu={e => setContextMenu({ nodeId:node.id, x:e.clientX, y:e.clientY })}
                onToggleLock={() => toggleNodeLock(node.id)}
                onPushToPlanner={(node.node_type==='checklist'||node.type==='checklist') ? () => pushChecklistToPlanner(node) : undefined}
                onCheckToggle={idx => {
                  const items = [...(Array.isArray(node.checked_items) ? node.checked_items : [])];
                  items[idx] = { ...items[idx], done: !items[idx].done };
                  updateNodeField(node.id, { checked_items: items });
                }}
                onAddCheckItem={() => {
                  const items = [...(Array.isArray(node.checked_items) ? node.checked_items : []), { text:'', done:false }];
                  updateNodeField(node.id, { checked_items: items });
                  setSelectedId(node.id); setSelectedIds([]); setEditingId(node.id); setEditingCheckIdx(items.length-1);
                }}
              />
            ))}
            {Object.values(cursors).map(c => <LiveCursor key={c.id} c={c}/>)}
          </div>

          {/* Floating color picker — positioned in canvas space, not inside card */}
          {(() => {
            const selNode = selectedId ? nodesRef.current.find(n=>n.id===selectedId) : null;
            const nt = selNode?.node_type || selNode?.type;
            if (!selNode || nt==='image' || nt==='section' || nt==='drawing') return null;
            return <FloatingColorPicker node={selNode} panX={panX} panY={panY} zoom={zoom}
              onColorChange={c => updateNodeField(selNode.id, {color:c})}/>;
          })()}

          {/* Context menu */}
          {contextMenu && (() => {
            const cmNode = nodesRef.current.find(n=>n.id===contextMenu.nodeId);
            if (!cmNode) return null;
            return <ContextMenu x={contextMenu.x} y={contextMenu.y}
              onClose={() => setContextMenu(null)}
              onDuplicate={() => { setContextMenu(null); duplicateNode(cmNode); }}
              onDelete={() => { setContextMenu(null); pushUndo(); deleteNode(cmNode.id); }}
              onBringToFront={() => { setContextMenu(null); bringToFront(cmNode.id); }}
              onSendToBack={() => { setContextMenu(null); sendToBack(cmNode.id); }}
              onToggleLock={() => { setContextMenu(null); toggleNodeLock(cmNode.id); }}
              isLocked={!!cmNode.locked}
              onLinkToBug={() => { setContextMenu(null); window.sanoireNav && window.sanoireNav('appstudio'); sessionStorage.setItem('studio_link_bug', JSON.stringify({ boardId: activeBoardRef.current, nodeId: cmNode.id, nodeText: (cmNode.content||'').slice(0,80) })); }}
              onPushToPlanner={() => { setContextMenu(null); if (cmNode.node_type==='checklist'||cmNode.type==='checklist') { pushChecklistToPlanner(cmNode); } else { const txt = (cmNode.content||'').slice(0,100); window.sanoireNav && window.sanoireNav('planner'); sessionStorage.setItem('studio_planner_task', txt); } }}/>;
          })()}

          {isEmpty && !mode && (
            <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', pointerEvents:'none', gap:10 }}>
              <div style={{ width:56, height:56, borderRadius:16, background:'rgba(49,79,92,0.07)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:4 }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="rgba(49,79,92,0.3)" strokeWidth="1.5" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/></svg>
              </div>
              <div style={{ fontSize:14, fontWeight:600, color:'rgba(49,79,92,0.35)', fontFamily:F }}>Board is empty</div>
              <div style={{ fontSize:12, color:'rgba(49,79,92,0.25)', fontFamily:F }}>Double-click anywhere to add a note · pick a template below</div>
              <div style={{ fontSize:11, color:'rgba(49,79,92,0.18)', fontFamily:F, marginTop:6, display:'flex', gap:10, justifyContent:'center' }}>
                <span>F fit</span><span>·</span><span>⌘Z undo</span><span>·</span><span>⌘D duplicate</span><span>·</span><span>Right-click menu</span>
              </div>
            </div>
          )}

          {mode && (
            <div style={{ position:'absolute', top:12, left:'50%', transform:'translateX(-50%)', background:'rgba(13,29,43,0.93)', color:'#AFE1F6', padding:'7px 20px', borderRadius:99, fontSize:11.5, fontWeight:600, fontFamily:F, display:'flex', alignItems:'center', gap:8, boxShadow:'0 4px 20px rgba(0,0,0,0.35)', border:'1px solid rgba(175,225,246,0.1)', pointerEvents:'none' }}>
              <div style={{ width:7, height:7, borderRadius:'50%', background:'#2da06a', animation:'studioPulse 1.5s ease-in-out infinite' }}/>
              {mode==='draw'
                ? 'Draw mode — click and drag to sketch'
                : connecting?.fromId
                  ? 'Click another node to connect  ·  click canvas to cancel'
                  : 'Connect mode — click a node to start'}
              <span style={{ opacity:.5, fontSize:10 }}>Esc to exit</span>
            </div>
          )}

          {uploadingImage && (
            <div style={{ position:'absolute', bottom:80, left:'50%', transform:'translateX(-50%)', background:'#0f1e28', color:'#E0F5FC', padding:'8px 18px', borderRadius:99, fontSize:12, fontWeight:600, fontFamily:F, display:'flex', alignItems:'center', gap:8, boxShadow:'0 4px 16px rgba(0,0,0,0.3)' }}>
              <div style={{ width:14, height:14, borderRadius:'50%', border:'2px solid rgba(175,225,246,0.2)', borderTop:'2px solid #AFE1F6', animation:'sanoireSpin .9s linear infinite' }}/>
              Uploading image…
            </div>
          )}

          {/* Minimap */}
          {nodes.length > 0 && !showSana && canvasRef.current && (
            <Minimap nodes={nodes} panX={panX} panY={panY} zoom={zoom}
              canvasW={canvasRef.current.getBoundingClientRect().width||800}
              canvasH={canvasRef.current.getBoundingClientRect().height||500}
              onJump={(bx,by)=>{ const r=canvasRef.current?.getBoundingClientRect(); if(!r) return; setPanX(r.width/2-bx*zoomRef.current); setPanY(r.height/2-by*zoomRef.current); }}/>
          )}

          {/* Search bar */}
          {showSearch && (
            <SearchBar query={searchQuery} onChange={q=>{setSearchQuery(q);setSearchIdx(0);}}
              onClose={()=>{setShowSearch(false);setSearchQuery('');}} resultCount={getSearchResults().length}
              current={searchIdx} onNext={()=>searchNext(1)} onPrev={()=>searchNext(-1)}/>
          )}

          <div style={{ position:'absolute', bottom:78, right:14, fontSize:10, fontWeight:600, color:'rgba(49,79,92,0.28)', fontFamily:F, background:'rgba(255,255,255,0.82)', padding:'3px 10px', borderRadius:99, pointerEvents:'none', boxShadow:'0 1px 5px rgba(15,30,40,0.08)' }}>
            {Math.round(zoom*100)}%{shiftKeyRef.current ? ' · snap' : ''}
          </div>

          <BottomPalette mode={mode}
            onSetMode={m => { setMode(m); setConnecting(null); setDrawPath(null); drawingRef.current=false; }}
            onAddNote={() => addNode('note')}
            onAddHeading={() => addNode('heading')}
            onAddChecklist={() => addNode('checklist')}
            onAddSection={() => addNode('section')}
            onAddLink={addLinkNode}
            onUploadImage={uploadImage}
            onShowTemplates={() => setShowTemplate(true)}/>
        </div>
      </div>

      {/* Sana AI panel */}
      {showSana && (
        <SanaPanel boardName={boards.find(b=>b.id===activeBoardId)?.name||'Board'}
          nodes={nodes} onClose={()=>setShowSana(false)} onDropToCanvas={dropSanaToCanvas}/>
      )}
    </div>

    {showShortcuts && <ShortcutsModal onClose={()=>setShowShortcuts(false)}/>}
    {showTemplate && nodes.length===0 && <TemplateModal onApply={applyTemplate} onSkip={()=>setShowTemplate(false)}/>}
    </>
  );
}

export default function Studio(props) {
  if (props && props.embed) return <StudioBody session={props.session} />;
  return <ConsoleShell title="Studio"><StudioBody session={props && props.session} /></ConsoleShell>;
}
